import { ShadowGenerator } from "@egr/wcf/modules/core/rendering/ShadowGenerator";
import { deserializeVector2, deserializeVector3 } from "@egr/wcf/modules/core/io";
import { Ray } from "@babylonjs/core/Culling/ray";
import { VertexBuffer } from "@babylonjs/core/Buffers/buffer";
import { VertexData } from "@babylonjs/core/Meshes/mesh.vertexData";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { Plane } from "@babylonjs/core/Maths/math.plane";
import { CreateLines } from "@babylonjs/core/Meshes/Builders/linesBuilder";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Vector2 } from "@babylonjs/core/Maths/math.vector";
import { Matrix } from "@babylonjs/core/Maths/math.vector";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { BoundingBox } from '@egr/wcf/modules/core/mdl';
import { MainArticleElement } from "@egr/wcf/modules/cf";
import { CsgUtils } from "../mdl/CsgUtils";
import { CoordinatesType } from "@egr/wcf/modules/core/view/View";
import { intersectRayPlane, VECTOR3_Y } from "@egr/wcf/modules/core/utils/MathUtils";
import { EPSILON, geq, clamp, eq, radToDeg, degToRad } from "@egr/wcf/modules/utils/math";
import { isCollinear2, orientationToLine2, Orientation, isPointOnSegment2, intersectLineLine2, isParallel2, signedAngleBetweenVectors2 } from "@egr/wcf/modules/core/utils/MathUtils";
import { contains, removeElement } from "@egr/wcf/modules/utils/array";
import { LengthUnit, toLocaleLengthString } from "@egr/wcf/modules/utils/number";
import { CameraMode } from "@egr/wcf/modules/core/view/CameraControl";
var AxisDir;
(function (AxisDir) {
    AxisDir[AxisDir["PositiveX"] = 0] = "PositiveX";
    AxisDir[AxisDir["PositiveY"] = 1] = "PositiveY";
    AxisDir[AxisDir["PositiveZ"] = 2] = "PositiveZ";
    AxisDir[AxisDir["NegativeX"] = 3] = "NegativeX";
    AxisDir[AxisDir["NegativeY"] = 4] = "NegativeY";
    AxisDir[AxisDir["NegativeZ"] = 5] = "NegativeZ";
})(AxisDir || (AxisDir = {}));
export class Util {
    /**
     * Delivers URL addon to avoid browser cache issues.
     * Format like: + "?v=636251
     */
    static URLrandomAddOn() {
        const tVal = "?v=" + (100000 * Math.random()).toFixed();
        return (tVal);
    }
    static deserializeV3(pV3) {
        let tPstr = JSON.stringify(pV3);
        tPstr = tPstr.replace('{', '[');
        tPstr = tPstr.replace('}', ']');
        tPstr = tPstr.replace('"x":', '');
        tPstr = tPstr.replace('"y":', '');
        tPstr = tPstr.replace('"z":', '');
        const tRes = deserializeVector3(JSON.parse(tPstr));
        return (tRes);
    }
    static deserializeV2(pV2) {
        let tPstr = JSON.stringify(pV2);
        tPstr = tPstr.replace('{', '[');
        tPstr = tPstr.replace('}', ']');
        tPstr = tPstr.replace('"x":', '');
        tPstr = tPstr.replace('"y":', '');
        const tRes = deserializeVector2(JSON.parse(tPstr));
        return (tRes);
    }
    static calcSnapPoint(pStart, pEnd, pAngle, pPrevStart, pPrevEnd) {
        let tRes = new Vector3(0, 0, 0);
        let tprevAngle = 0;
        if (pPrevStart != null && pPrevEnd != null) {
            const tprevDeltaX = pPrevEnd.x - pPrevStart.x;
            const tprevDeltaY = pPrevEnd.z - pPrevStart.z;
            tprevAngle = Math.atan2(tprevDeltaY, tprevDeltaX);
        }
        const tAngle = clamp(pAngle, 0.1, 180);
        const tDiv = 180 / tAngle;
        const tdeltaX = pEnd.x - pStart.x;
        const tdeltaY = pEnd.z - pStart.z;
        const tdist = Math.sqrt(Math.pow(tdeltaX, 2) + Math.pow(tdeltaY, 2));
        const tnewAngle = Math.atan2(tdeltaY, tdeltaX);
        const tnewAngleDEG = radToDeg(tnewAngle);
        const tshiftedAngle = (Math.round(tnewAngle / Math.PI * tDiv) / tDiv * Math.PI) - tprevAngle;
        tRes.x = pStart.x + tdist * Math.cos(tshiftedAngle);
        tRes.z = pStart.z + tdist * Math.sin(tshiftedAngle);
        return (tRes);
    }
    static rotateVectorSnapping(pVprev, pVcurr, pSnapAngleDeg) {
        const tLprev_S = pVprev.Start;
        const tLprev_E = pVprev.End;
        const tLcurr_S = pVcurr.Start;
        const tLcurr_E = pVcurr.End;
        const tLP = tLprev_S.subtract(tLprev_E);
        const tLP2d = Util.to2d(tLP);
        const tLC = tLcurr_E.subtract(tLcurr_S);
        const tLC2d = Util.to2d(tLC);
        const tangleRAD = signedAngleBetweenVectors2(tLP2d, tLC2d);
        const tangleDEG = radToDeg(tangleRAD);
        const tnextAngleMOD = Math.round(tangleDEG / pSnapAngleDeg);
        const tangleMOD = pSnapAngleDeg * tnextAngleMOD;
        const tsnapAngle = tangleMOD - tangleDEG;
        var tsnapAngleRAD = degToRad(-tsnapAngle);
        var tangleCOS = Math.cos(tsnapAngleRAD);
        var tangleSIN = Math.sin(tsnapAngleRAD);
        var tnewX = (tangleCOS * (tLcurr_E.x - tLcurr_S.x)) + (tangleSIN * (tLcurr_E.z - tLcurr_S.z)) + tLcurr_S.x;
        var tnewY = (tangleCOS * (tLcurr_E.z - tLcurr_S.z)) - (tangleSIN * (tLcurr_E.x - tLcurr_S.x)) + tLcurr_S.z;
        let tRes = new Vector3(tnewX, 0, tnewY);
        return (tRes);
    }
    static matMultVec(pMatrix, pVector) {
        let tRes = new Vector3(0, 0, 0);
        let tx = pMatrix.m[0] * pVector.x + pMatrix.m[1] * pVector.y + pMatrix.m[2] * pVector.z + pMatrix.m[3];
        let ty = pMatrix.m[4] * pVector.x + pMatrix.m[5] * pVector.y + pMatrix.m[6] * pVector.z + pMatrix.m[7];
        let tz = pMatrix.m[8] * pVector.x + pMatrix.m[9] * pVector.y + pMatrix.m[10] * pVector.z + pMatrix.m[11];
        let tw = pMatrix.m[12] * pVector.x + pMatrix.m[13] * pVector.y + pMatrix.m[14] * pVector.z + pMatrix.m[15];
        tRes = new Vector3(tx, ty, tz);
        tRes.x *= tw;
        tRes.y *= tw;
        tRes.z *= tw;
        return (tRes);
    }
    static matVecToPnt(pVector) {
        let tRes = pVector.clone();
        tRes.x *= 100;
        tRes.z *= 100;
        return (tRes);
    }
    static matPntToVec(pVector) {
        let tRes = pVector.clone();
        tRes.x /= 100;
        tRes.z /= 100;
        return (tRes);
    }
    static Initialize(pCoreApp) {
        this.mApp = pCoreApp;
    }
    static GetFilenameFromURL(pURL) {
        if (pURL.trim() !== "") {
            let tmatchStr = pURL.toString().match(/.*\/(.+?)\./);
            if (tmatchStr && tmatchStr.length > 1) {
                return (tmatchStr[1]);
            }
        }
        return ("");
    }
    static checkGridFieldPointsInPolygon(pGridPolygon, pPoints) {
        let tPoPCnt = 0;
        for (let tP of pPoints) {
            if (Util.pointInPolygon(tP, pGridPolygon)) {
                tPoPCnt++;
            }
        }
        if (tPoPCnt === pPoints.length) {
            return (PointOnPolygons.all);
        }
        if (tPoPCnt > 0 && tPoPCnt < pPoints.length) {
            return (PointOnPolygons.some);
        }
        return (PointOnPolygons.none);
    }
    static checkGridFieldPointsInMesh(pMesh, ptransMatrix, pPoints, pScene) {
        let tPoPCnt = 0;
        const tmeshH = pMesh.absolutePosition.clone().y;
        for (let tP of pPoints) {
            const trayPos = new Vector3(tP.x, tmeshH + 0.01, tP.y);
            const ttransP = Vector3.TransformCoordinates(trayPos, ptransMatrix);
            if (Util.pointInMesh(ttransP, pMesh, pScene)) {
                tPoPCnt++;
            }
        }
        if (tPoPCnt === pPoints.length) {
            return (PointOnPolygons.all);
        }
        if (tPoPCnt > 0 && tPoPCnt < pPoints.length) {
            return (PointOnPolygons.some);
        }
        return (PointOnPolygons.none);
    }
    static checkGridFieldPointsInMesh_Count(pMesh, ptransMatrix, pPoints, pScene) {
        let tPoPCnt = 0;
        const tmeshH = pMesh.absolutePosition.clone().y;
        for (let tP of pPoints) {
            const trayPos = new Vector3(tP.x, tmeshH + 0.01, tP.y);
            const ttransP = Vector3.TransformCoordinates(trayPos, ptransMatrix);
            if (Util.pointInMesh(ttransP, pMesh, pScene)) {
                tPoPCnt++;
            }
        }
        return (tPoPCnt);
    }
    static getGridFieldCount(pGrid) {
        if (pGrid.type !== "Grid") {
            return (0);
        }
        const tW = pGrid.width;
        const tD = pGrid.depth;
        const tgX = pGrid.gridCellSize.x;
        const tgZ = pGrid.gridCellSize.y;
        if (tW <= 0 || tD <= 0 || tgX <= 0 || tgZ <= 0) {
            return (0);
        }
        const tcntX = Math.round(tW / tgX);
        const tcntZ = Math.round(tD / tgZ);
        return (tcntX * tcntZ);
    }
    static getGridAllFieldPoints(pGrid, pIdxX, pIdxZ) {
        if (pGrid.type !== "Grid") {
            return (null);
        }
        let tRes = [];
        let tgXH = pGrid.gridCellSize.x * 0.5;
        const tgYH = pGrid.gridCellSize.y * 0.5;
        const tgXQ = tgXH * 0.5;
        const tgYQ = tgYH * 0.5;
        const tW = pGrid.width;
        const tD = pGrid.depth;
        const tWH = tW * 0.5;
        const tDH = tD * 0.5;
        let tcntX = 1;
        let tcntZ = 1;
        let tlz = true;
        for (let z = tDH; z >= -tDH - tgYQ; z -= tgYH) {
            tgXH = tlz ? pGrid.gridCellSize.x * 0.5 : pGrid.gridCellSize.x;
            tcntX = 1;
            for (let x = -tWH; x <= tWH + tgXQ; x += tgXH) {
                let tsp = new Vector3(x, 0, z);
                const tsp2d = Util.to2d(tsp);
                const tminX = 2 * pIdxX - 1;
                const tmaxX = 2 * (pIdxX + 1) - 1;
                const tminZ = 2 * pIdxZ - 1;
                const tmaxZ = 2 * (pIdxZ + 1) - 1;
                if (tcntX >= tminX && tcntX <= tmaxX &&
                    tcntZ >= tminZ && tcntZ <= tmaxZ) {
                    tRes.push(tsp2d);
                }
                tcntX++;
                if (!tlz) {
                    tcntX++;
                }
            }
            tlz = !tlz;
            tcntZ++;
        }
        return (tRes);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    static getGridModeFieldPoints(pAllPoints, pMode) {
        if (pAllPoints.length < 8) {
            return (null);
        }
        let tRes = [];
        switch (pMode) {
            case GridFieldPointMode.all:
                tRes = Util.getGridModeField_Rectangle(pAllPoints[0], pAllPoints[5], pAllPoints[7], pAllPoints[2]);
                break;
            case GridFieldPointMode.full:
                tRes = Util.getGridModeField_Rectangle(pAllPoints[0], pAllPoints[5], pAllPoints[7], pAllPoints[2]);
                break;
            case GridFieldPointMode.diagonalTopLeft:
                tRes = Util.getGridModeField_Triangle(pAllPoints[0], pAllPoints[5], pAllPoints[2]);
                break;
            case GridFieldPointMode.diagonalTopRight:
                tRes = Util.getGridModeField_Triangle(pAllPoints[0], pAllPoints[7], pAllPoints[2]);
                break;
            case GridFieldPointMode.diagnoalBottomLeft:
                tRes = Util.getGridModeField_Triangle(pAllPoints[0], pAllPoints[5], pAllPoints[7]);
                break;
            case GridFieldPointMode.diagnoalBottomRight:
                tRes = Util.getGridModeField_Triangle(pAllPoints[5], pAllPoints[7], pAllPoints[2]);
                break;
            case GridFieldPointMode.horizontalTop:
                tRes = Util.getGridModeField_Rectangle(pAllPoints[0], pAllPoints[3], pAllPoints[4], pAllPoints[2]);
                break;
            case GridFieldPointMode.horizontalBottom:
                tRes = Util.getGridModeField_Rectangle(pAllPoints[3], pAllPoints[5], pAllPoints[7], pAllPoints[4]);
                break;
            case GridFieldPointMode.verticalLeft:
                tRes = Util.getGridModeField_Rectangle(pAllPoints[0], pAllPoints[5], pAllPoints[6], pAllPoints[1]);
                break;
            case GridFieldPointMode.verticalRight:
                tRes = Util.getGridModeField_Rectangle(pAllPoints[1], pAllPoints[6], pAllPoints[7], pAllPoints[2]);
                break;
            default:
                tRes = null;
                break;
        }
        return (tRes);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    static getGridModeFieldCAG(pAllPoints, pMode) {
        if (pAllPoints.length < 8) {
            return (null);
        }
        let tPoints = new Array();
        switch (pMode) {
            case GridFieldPointMode.all:
            case GridFieldPointMode.full:
                tPoints.push(new CSG.Vector2D(pAllPoints[0].x, pAllPoints[0].y));
                tPoints.push(new CSG.Vector2D(pAllPoints[5].x, pAllPoints[5].y));
                tPoints.push(new CSG.Vector2D(pAllPoints[7].x, pAllPoints[7].y));
                tPoints.push(new CSG.Vector2D(pAllPoints[2].x, pAllPoints[2].y));
                break;
            case GridFieldPointMode.diagonalTopLeft:
                tPoints.push(new CSG.Vector2D(pAllPoints[0].x, pAllPoints[0].y));
                tPoints.push(new CSG.Vector2D(pAllPoints[5].x, pAllPoints[5].y));
                tPoints.push(new CSG.Vector2D(pAllPoints[2].x, pAllPoints[2].y));
                break;
            case GridFieldPointMode.diagonalTopRight:
                tPoints.push(new CSG.Vector2D(pAllPoints[0].x, pAllPoints[0].y));
                tPoints.push(new CSG.Vector2D(pAllPoints[7].x, pAllPoints[7].y));
                tPoints.push(new CSG.Vector2D(pAllPoints[2].x, pAllPoints[2].y));
                break;
            case GridFieldPointMode.diagnoalBottomLeft:
                tPoints.push(new CSG.Vector2D(pAllPoints[0].x, pAllPoints[0].y));
                tPoints.push(new CSG.Vector2D(pAllPoints[5].x, pAllPoints[5].y));
                tPoints.push(new CSG.Vector2D(pAllPoints[7].x, pAllPoints[7].y));
                break;
            case GridFieldPointMode.diagnoalBottomRight:
                tPoints.push(new CSG.Vector2D(pAllPoints[5].x, pAllPoints[5].y));
                tPoints.push(new CSG.Vector2D(pAllPoints[7].x, pAllPoints[7].y));
                tPoints.push(new CSG.Vector2D(pAllPoints[2].x, pAllPoints[2].y));
                break;
            case GridFieldPointMode.horizontalTop:
                tPoints.push(new CSG.Vector2D(pAllPoints[0].x, pAllPoints[0].y));
                tPoints.push(new CSG.Vector2D(pAllPoints[3].x, pAllPoints[3].y));
                tPoints.push(new CSG.Vector2D(pAllPoints[4].x, pAllPoints[4].y));
                tPoints.push(new CSG.Vector2D(pAllPoints[2].x, pAllPoints[2].y));
                break;
            case GridFieldPointMode.horizontalBottom:
                tPoints.push(new CSG.Vector2D(pAllPoints[3].x, pAllPoints[3].y));
                tPoints.push(new CSG.Vector2D(pAllPoints[5].x, pAllPoints[5].y));
                tPoints.push(new CSG.Vector2D(pAllPoints[7].x, pAllPoints[7].y));
                tPoints.push(new CSG.Vector2D(pAllPoints[4].x, pAllPoints[4].y));
                break;
            case GridFieldPointMode.verticalLeft:
                tPoints.push(new CSG.Vector2D(pAllPoints[0].x, pAllPoints[0].y));
                tPoints.push(new CSG.Vector2D(pAllPoints[5].x, pAllPoints[5].y));
                tPoints.push(new CSG.Vector2D(pAllPoints[6].x, pAllPoints[6].y));
                tPoints.push(new CSG.Vector2D(pAllPoints[1].x, pAllPoints[1].y));
                break;
            case GridFieldPointMode.verticalRight:
                tPoints.push(new CSG.Vector2D(pAllPoints[1].x, pAllPoints[1].y));
                tPoints.push(new CSG.Vector2D(pAllPoints[6].x, pAllPoints[6].y));
                tPoints.push(new CSG.Vector2D(pAllPoints[7].x, pAllPoints[7].y));
                tPoints.push(new CSG.Vector2D(pAllPoints[2].x, pAllPoints[2].y));
                break;
            default:
                break;
        }
        const tCAG = CAG.fromPointsNoCheck(tPoints);
        return (tCAG);
    }
    // p0 p7 p6
    // p1    p5
    // p2 p3 p4
    static getGridModeField_All(pP0, pP1, pP2, pP3, pP4, pP5, pP6, pP7) {
        const tbiSecP0 = Util.getBisector(pP0, pP7, pP1);
        const tnewP0 = pP0.add(tbiSecP0.scale(0.001));
        const tnewP1 = pP1.add(new Vector2(1, 0).scale(0.001));
        const tbiSecP2 = Util.getBisector(pP2, pP1, pP3);
        const tnewP2 = pP2.add(tbiSecP2.scale(0.001));
        const tnewP3 = pP3.add(new Vector2(0, 1).scale(0.001));
        const tbiSecP4 = Util.getBisector(pP4, pP3, pP5);
        const tnewP4 = pP4.add(tbiSecP2.scale(0.001));
        const tnewP5 = pP5.add(new Vector2(-1, 0).scale(0.001));
        const tbiSecP6 = Util.getBisector(pP4, pP5, pP7);
        const tnewP6 = pP6.add(tbiSecP6.scale(0.001));
        const tnewP7 = pP7.add(new Vector2(0, -1).scale(0.001));
        const tRes = [tnewP0, tnewP1, tnewP2, tnewP3, tnewP4, tnewP5, tnewP6, tnewP7];
        return (tRes);
    }
    // p0    p3
    //
    // p1    p2
    static getGridModeField_Rectangle(pP0, pP1, pP2, pP3) {
        const tbiSecP0 = Util.getBisector(pP0, pP3, pP1);
        const tnewP0 = pP0.add(tbiSecP0.scale(0.001));
        const tbiSecP1 = Util.getBisector(pP1, pP0, pP2);
        const tnewP1 = pP1.add(tbiSecP1.scale(0.001));
        const tbiSecP2 = Util.getBisector(pP2, pP1, pP3);
        const tnewP2 = pP2.add(tbiSecP2.scale(0.001));
        const tbiSecP3 = Util.getBisector(pP3, pP2, pP0);
        const tnewP3 = pP3.add(tbiSecP3.scale(0.001));
        const tRes = [tnewP0, tnewP1, tnewP2, tnewP3];
        return (tRes);
    }
    // p0----p2
    //    \  |
    //       p1
    static getGridModeField_Triangle(pP0, pP1, pP2) {
        const tbiSecP0 = Util.getBisector(pP0, pP2, pP1);
        const tnewP0 = pP0.add(tbiSecP0.scale(0.001));
        const tbiSecP1 = Util.getBisector(pP1, pP0, pP2);
        const tnewP1 = pP1.add(tbiSecP1.scale(0.001));
        const tbiSecP2 = Util.getBisector(pP2, pP1, pP0);
        const tnewP2 = pP2.add(tbiSecP2.scale(0.001));
        const tRes = [tnewP0, tnewP1, tnewP2];
        return (tRes);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    static getGridFieldCell(pGrid, pIdx) {
        if (pGrid.type !== "Grid") {
            return (null);
        }
        const ttotalGridCount = Util.getGridFieldCount(pGrid);
        if (ttotalGridCount === 0 || pIdx === 0 || pIdx > ttotalGridCount) {
            return (null);
        }
        const tW = pGrid.width;
        const tD = pGrid.depth;
        let tgX = pGrid.gridCellSize.x;
        const tgZ = pGrid.gridCellSize.y;
        if (tW <= 0 || tD <= 0 || tgX <= 0 || tgZ <= 0) {
            return (null);
        }
        const tcntX = Math.round(tW / tgX);
        let tcol = pIdx % tcntX;
        let trow = Math.floor(pIdx / tcntX);
        if (tcol !== 0) {
            trow++;
        }
        else {
            tcol = tcntX;
        }
        const tRes = new Vector2(tcol, trow);
        return (tRes);
    }
    static getImgExt(pDataURL) {
        let tIdx = pDataURL.trim().indexOf("data:image/");
        if (tIdx !== 0) {
            // no image type found; return ".jpg"
            return (".jpg");
        }
        let tsemiIdx = pDataURL.trim().indexOf(";");
        let tExt = pDataURL.substr(11, tsemiIdx - 11).toLocaleLowerCase();
        if (tExt.indexOf("bmp") > -1) {
            tExt = "bmp";
        }
        if (tExt.indexOf("png") > -1) {
            tExt = "png";
        }
        if (tExt.indexOf("jpg") > -1) {
            tExt = "jpg";
        }
        if (tExt.indexOf("jpeg") > -1) {
            tExt = "jpg";
        }
        return (tExt);
    }
    static setMaterialData(pId, pMaterial, psplitStr) {
        if (pId.trim() === "")
            return;
        pMaterial.loadMatId = pId;
        let tmatId = pId.split(psplitStr);
        let tmatValue = tmatId.pop();
        let tmatCategory = tmatId.pop();
        if (tmatCategory != null && (tmatCategory.trim() !== "color")) {
            pMaterial.category = tmatCategory.trim();
        }
        if (tmatValue != null) {
            pMaterial.value = tmatValue.trim();
        }
    }
    static CAG_createBox(pWidth, pDepth, pHeight, pLeft, pScene) {
        if (pWidth <= 0 || pDepth <= 0 || pHeight < 0)
            return (null);
        let tWH = pWidth * 0.5;
        let tDH = pDepth * 0.5;
        let tHH = pHeight * 0.5;
        let tPoints = new Array();
        tPoints.push(new CSG.Vector2D(0, 0));
        tPoints.push(new CSG.Vector2D(pWidth, 0));
        tPoints.push(new CSG.Vector2D(pWidth, pDepth));
        tPoints.push(new CSG.Vector2D(0, pDepth));
        let tCAGshape = CAG.fromPointsNoCheck(tPoints);
        if (tCAGshape.isSelfIntersecting())
            return (null);
        let tCSG = Util.CAG2CSG(tCAGshape, pHeight);
        let tMeshes = CsgUtils.buildMeshes(tCSG, pScene);
        if (tMeshes == null)
            return (null);
        let tRes = Mesh.MergeMeshes(tMeshes);
        if (tRes == null)
            return (null);
        tRes.position.x -= tWH;
        tRes.position.z -= tDH;
        if (!pLeft) {
            tRes.scaling.x *= -1;
            tRes.position.x = tWH * 2;
            tRes.position.z = 0;
            tRes.bakeCurrentTransformIntoVertices();
        }
        return (tRes);
    }
    static CAG_createPlane(pWidth, pDepth, pHeight, pScene) {
        if (pWidth <= 0 || pDepth <= 0 || pHeight < 0)
            return (null);
        let tWH = pWidth * 0.5;
        let tDH = pDepth * 0.5;
        let tHH = pHeight * 0.5;
        let tPoints = new Array();
        tPoints.push(new CSG.Vector2D(-tWH, -tDH));
        tPoints.push(new CSG.Vector2D(tWH, -tDH));
        tPoints.push(new CSG.Vector2D(tWH, tDH));
        tPoints.push(new CSG.Vector2D(-tWH, tDH));
        let tCAGshape = CAG.fromPointsNoCheck(tPoints);
        if (tCAGshape.isSelfIntersecting())
            return (null);
        let tCSG = Util.CAG2CSG(tCAGshape, pHeight);
        let tMeshes = CsgUtils.buildMeshes(tCSG, pScene);
        if (tMeshes == null)
            return (null);
        let tRes = Mesh.MergeMeshes(tMeshes);
        if (tRes == null)
            return (null);
        tRes.position.x -= tWH;
        tRes.position.z -= tDH;
        return (tRes);
    }
    static getDestClonePositon(pSelection) {
        let tRes = null;
        if (pSelection.length === 0)
            return (null);
        let tBB = BoundingBox.FromElements(pSelection, true, true);
        let tBB_center3d = tBB.getCenter();
        tBB_center3d.y = 0;
        let tl_Bottom = new Vector3(tBB.min.x, 0, tBB.min.z);
        let tr_Bottom = new Vector3(tBB.max.x, 0, tBB.min.z);
        let tr_Top = new Vector3(tBB.max.x, 0, tBB.max.z);
        let tr_center = Vector3.Center(tr_Bottom, tr_Top);
        let tBB_width = Vector3.Distance(tl_Bottom, tr_Bottom);
        let tm_right = (tr_center.subtract(tBB_center3d).normalize()).scale(tBB_width);
        tRes = Util.to2d(tBB_center3d.add(tm_right));
        return (tRes);
    }
    static getViewCenter(pView) {
        let tPickPos = null;
        let tPickPlane = null;
        if (pView.cameraControl.cameraMode == CameraMode.Orthographic ||
            pView.cameraControl.cameraMode == CameraMode.Perspective) {
            tPickPlane = Plane.FromPositionAndNormal(Vector3.Zero(), VECTOR3_Y);
        }
        else {
            tPickPlane = Plane.FromPositionAndNormal(Vector3.Zero(), pView.cameraControl.direction);
        }
        let tRay = pView.createPickingRay(new Vector2(pView.width * 0.5, pView.height * 0.5), CoordinatesType.Viewer);
        tPickPos = intersectRayPlane(tRay, tPickPlane);
        if (tPickPos == null) {
            tPickPos = Vector3.Zero();
        }
        return (tPickPos);
    }
    static getViewScaleFactorOfNode(pView, pNode, pWorldRadius, pPixelRadius) {
        let tRes = null;
        if (pView == null || pNode == null)
            return (tRes);
        let tProj1 = pView.projectPoint2(pNode.position, CoordinatesType.Viewer, null, true);
        let tPos = Vector3.Zero();
        pView.cameraControl.right.scaleToRef(pWorldRadius, tPos);
        pNode.position.addToRef(tPos, tPos);
        let tProj2 = pView.projectPoint2(tPos, CoordinatesType.Viewer, null, true);
        if (tProj1 != null && tProj2 != null) {
            let tV = tProj1.subtract(tProj2);
            tRes = pPixelRadius / tV.length();
        }
        return (tRes);
    }
    static getWallCheckLine(pWall) {
        let tRes = new Array();
        let tdirection = pWall.wallDirection;
        tRes.push(pWall.startPoint.subtract(tdirection.scale(500)));
        tRes.push(pWall.startPoint.add(tdirection.scale(500)));
        return (tRes);
    }
    static wallsHaveSamePoint(pWall1, pWall2) {
        if (pWall1 == null || pWall2 == null)
            return (false);
        if (Util.samePointsV2(pWall1.startPoint, pWall2.startPoint, EPSILON) ||
            Util.samePointsV2(pWall1.startPoint, pWall2.endPoint, EPSILON) ||
            Util.samePointsV2(pWall1.endPoint, pWall2.startPoint, EPSILON) ||
            Util.samePointsV2(pWall1.endPoint, pWall2.endPoint, EPSILON))
            return (true);
        return (false);
    }
    static wallsAreCollinear(pWall1, pWall2) {
        let tRes = false;
        if (pWall1 == null || pWall2 == null)
            return (false);
        if (isCollinear2(pWall1.startPoint, pWall1.endPoint, pWall2.startPoint) &&
            isCollinear2(pWall1.startPoint, pWall1.endPoint, pWall2.endPoint))
            tRes = true;
        return (tRes);
    }
    static getWallEndingPoints(pInitWall, pConnectedWalls) {
        let tRes = new Array();
        let tcheckLine = Util.getWallCheckLine(pInitWall);
        let tminDistS = Vector2.Distance(tcheckLine[0], tcheckLine[1]);
        let tminDistE = tminDistS;
        for (let tWall of pConnectedWalls) {
            let tDistS = Vector2.Distance(tcheckLine[0], tWall.startPoint);
            if (tDistS < tminDistS) {
                tminDistS = tDistS;
                tRes[0] = tWall.startPoint.clone();
            }
            tDistS = Vector2.Distance(tcheckLine[0], tWall.endPoint);
            if (tDistS < tminDistS) {
                tminDistS = tDistS;
                tRes[0] = tWall.endPoint.clone();
            }
            let tDistE = Vector2.Distance(tcheckLine[1], tWall.startPoint);
            if (tDistE < tminDistE) {
                tminDistE = tDistE;
                tRes[1] = tWall.startPoint.clone();
            }
            tDistE = Vector2.Distance(tcheckLine[1], tWall.endPoint);
            if (tDistE < tminDistE) {
                tminDistE = tDistE;
                tRes[1] = tWall.endPoint.clone();
            }
        }
        return (tRes);
    }
    static getmaxWallPoints(pWall1, pWall2) {
        let tRes = null;
        if (pWall1.startCxn.wall !== pWall2 && pWall1.endCxn.wall !== pWall2) {
            if (Util.vectorEquals2(pWall1.startPoint, pWall2.startPoint)) {
                tRes = new Array();
                tRes.push(pWall1.endPoint);
                tRes.push(pWall2.endPoint);
            }
            if (Util.vectorEquals2(pWall1.startPoint, pWall2.endPoint)) {
                tRes = new Array();
                tRes.push(pWall1.endPoint);
                tRes.push(pWall2.startPoint);
            }
            if (Util.vectorEquals2(pWall1.endPoint, pWall2.startPoint)) {
                tRes = new Array();
                tRes.push(pWall1.startPoint);
                tRes.push(pWall2.endPoint);
            }
            if (Util.vectorEquals2(pWall1.endPoint, pWall2.endPoint)) {
                tRes = new Array();
                tRes.push(pWall1.startPoint);
                tRes.push(pWall2.startPoint);
            }
            return (tRes);
        }
        if (pWall1.startCxn.wall === pWall2) {
            if (Util.vectorEquals2(pWall1.startPoint, pWall2.endPoint)) {
                tRes = new Array();
                tRes.push(pWall1.endPoint);
                tRes.push(pWall2.startPoint);
            }
            else {
                tRes = new Array();
                tRes.push(pWall1.endPoint);
                tRes.push(pWall2.endPoint);
            }
        }
        else if (Util.vectorEquals2(pWall1.endPoint, pWall2.endPoint)) {
            tRes = new Array();
            tRes.push(pWall1.startPoint);
            tRes.push(pWall2.startPoint);
        }
        else {
            tRes = new Array();
            tRes.push(pWall1.startPoint);
            tRes.push(pWall2.endPoint);
        }
        return (tRes);
    }
    static getImgExtensionFromDataURI(pDataURI) {
        let tIdx = pDataURI.trim().indexOf("data:image/");
        if (tIdx !== 0) {
            // no image type found; return ".jpg"
            return (".jpg");
        }
        {
            let tsemiIdx = pDataURI.trim().indexOf(";");
            let tExt = pDataURI.substr(11, tsemiIdx - 11).toLocaleLowerCase();
            if (tExt.indexOf("bmp") > -1) {
                tExt = "bmp";
            }
            if (tExt.indexOf("png") > -1) {
                tExt = "png";
            }
            if (tExt.indexOf("jpg") > -1) {
                tExt = "jpg";
            }
            if (tExt.indexOf("jpeg") > -1) {
                tExt = "jpg";
            }
            return (tExt);
        }
    }
    static leadingZero(pSource, pDestLength) {
        let tRes = pSource;
        while (tRes.length !== pDestLength) {
            tRes = "0" + tRes;
        }
        return (tRes);
    }
    static MATcolor2BABYLONColor3(pColorStr) {
        let tRes = Color3.FromHexString("#000000");
        let tsplitStr = pColorStr.split(" ");
        if (tsplitStr.length === 3) {
            let tB = parseFloat(tsplitStr.pop());
            let tG = parseFloat(tsplitStr.pop());
            let tR = parseFloat(tsplitStr.pop());
            let tRhex = Util.leadingZero((Math.round(tR * 255)).toString(16), 2);
            let tGhex = Util.leadingZero((Math.round(tG * 255)).toString(16), 2);
            let tBhex = Util.leadingZero((Math.round(tB * 255)).toString(16), 2);
            tRes = Color3.FromHexString("#" + tRhex + tGhex + tBhex);
        }
        return (tRes);
    }
    static StrToGfjVector3(pColorStr) {
        let tRes;
        let tsplitStr = pColorStr.split(" ");
        if (tsplitStr.length === 3) {
            tRes = [0, 0, 0];
            tRes[2] = parseFloat(tsplitStr.pop());
            tRes[1] = parseFloat(tsplitStr.pop());
            tRes[0] = parseFloat(tsplitStr.pop());
        }
        return (tRes);
    }
    static StrToGfjVector2(pColorStr) {
        let tRes;
        let tsplitStr = pColorStr.split(" ");
        if (tsplitStr.length === 2) {
            tRes = [0, 0];
            tRes[1] = parseFloat(tsplitStr.pop());
            tRes[0] = parseFloat(tsplitStr.pop());
        }
        return (tRes);
    }
    static buildMeshOfCAG(pCAG, pScene) {
        let tRes = null;
        if (pCAG == null)
            return (tRes);
        let tCSG = Util.CAG2CSG(pCAG);
        let tMeshes = CsgUtils.buildMeshes(tCSG, pScene);
        if (tMeshes != null) {
            for (let tMesh of tMeshes) {
                ShadowGenerator.disableMeshShadow(tMesh);
            }
        }
        if (tMeshes != null) {
            tRes = Mesh.MergeMeshes(tMeshes);
        }
        return (tRes);
    }
    static HashString(pStr) {
        let ttoHash = "";
        if (pStr === undefined) {
            let tResult = '';
            let tCharacters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
            let tcharLength = tCharacters.length;
            for (let i = 0; i < 15; i++) {
                tResult += tCharacters.charAt(Math.floor(Math.random() * tcharLength));
            }
            ttoHash = tResult;
        }
        else {
            ttoHash = pStr;
        }
        let tHash = 0;
        let tchr = 0;
        if (ttoHash.length === 0)
            return (tHash.toString());
        for (let i = 0; i < ttoHash.length; i++) {
            tchr = ttoHash.charCodeAt(i);
            tHash = ((tHash << 5) - tHash) + tchr;
            tHash = tHash & tHash;
        }
        return (tHash.toString());
    }
    static buildMeshOfPoints(pPoints, pScene) {
        let tRes = null;
        if (pPoints == null || (pPoints.length <= 2))
            return (tRes);
        let tCAGshape = CAG.fromPointsNoCheck(pPoints);
        if (tCAGshape.isSelfIntersecting())
            return (tRes);
        let tCSG = Util.CAG2CSG(tCAGshape, 0);
        let tMeshes = CsgUtils.buildMeshes(tCSG, pScene);
        tRes = tMeshes;
        return (tRes);
    }
    static removeBottomTopOfPolygon(pCSG, pHeight) {
        let tnewCSG = new CSG();
        for (let tPoly of pCSG.polygons) {
            let tBottom = true;
            let tTop = true;
            for (let tVert of tPoly.vertices) {
                if (!Util.numbersEqual(tVert.pos.y, pHeight, 0.00001)) {
                    tTop = false;
                    break;
                }
            }
            for (let tVert of tPoly.vertices) {
                if (!Util.numbersEqual(tVert.pos.y, 0, 0.00001)) {
                    tBottom = false;
                    break;
                }
            }
            if (!tTop && !tBottom) {
                tnewCSG.polygons.push(tPoly);
            }
        }
        return (tnewCSG);
    }
    static getTopPolygon(pCSG, pHeight) {
        let tnewCSG = new CSG();
        let ttoDelete = new Array();
        for (let tPoly of pCSG.polygons) {
            let tuniqeHeight = true;
            for (let tVert of tPoly.vertices) {
                if (!Util.numbersEqual(tVert.pos.y, pHeight, 0.00001)) {
                    tuniqeHeight = false;
                    break;
                }
            }
            if (tuniqeHeight) {
                tnewCSG.polygons.push(tPoly);
            }
        }
        return (tnewCSG);
    }
    static intersect2Circles(pCenter1, pRadius1, pCenter2, pRadius2) {
        let tRes = {};
        tRes.P1 = false;
        tRes.P2 = false;
        let tdiffX = pCenter2.x - pCenter1.x;
        let tdiffZ = pCenter2.z - pCenter1.z;
        let tcirlceDistance = Vector3.Distance(pCenter1, pCenter2);
        if (Util.numbersEqual(tcirlceDistance, 0, 0.0001))
            return (tRes);
        let x = (pRadius1 * pRadius1 + tcirlceDistance * tcirlceDistance - pRadius2 * pRadius2) / (2 * tcirlceDistance);
        let y = pRadius1 * pRadius1 - x * x;
        if (y < 0) {
            // no intersection
            return (tRes);
        }
        if (y > 0)
            y = Math.sqrt(y);
        // compute unit vectors ex and ey
        let ex0 = tdiffX / tcirlceDistance;
        let ex1 = tdiffZ / tcirlceDistance;
        let ey0 = -ex1;
        let ey1 = ex0;
        let Q1x = pCenter1.x + x * ex0;
        let Q1y = pCenter1.z + x * ex1;
        if (y === 0) {
            // one touch point
            tRes.P1 = new Vector3(Q1x, 0, Q1y);
            return (tRes);
        }
        // two intersections
        let Q2x = Q1x - y * ey0;
        let Q2y = Q1y - y * ey1;
        Q1x += y * ey0;
        Q1y += y * ey1;
        tRes.P1 = new Vector3(Q1x, 0, Q1y);
        tRes.P1 = new Vector3(Q2x, 0, Q2y);
        return (tRes);
    }
    static randomString(pLength) {
        let tText = "";
        let tpossibleSigns = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
        for (let i = 0; i < pLength; i++)
            tText += tpossibleSigns.charAt(Math.floor(Math.random() * tpossibleSigns.length));
        return tText;
    }
    static addCAG(pSource, pHole) {
        let tSource = pSource;
        let tsidesSource = pSource.sides;
        let tsidesHole = pHole.sides;
        for (let tSide of tsidesHole) {
            if (contains(tsidesSource, tSide))
                removeElement(tsidesSource, tSide);
        }
        tSource.sides = tsidesSource;
        return (tSource);
    }
    static getCAGMeasure(pCAG) {
        let tRes = new Vector3(0, 0, 0);
        tRes.y = 0.01;
        let tCAGbounds = pCAG.getBounds();
        let tWidth = Math.abs(tCAGbounds[0].x) + Math.abs(tCAGbounds[1].x);
        let tDepth = Math.abs(tCAGbounds[0].y) + Math.abs(tCAGbounds[1].y);
        tRes.x = tWidth;
        tRes.z = tDepth;
        return (tRes);
    }
    static correctPolygonDirection3(pPolygon) {
        let tRes = [];
        let tClockwise = Util._clockWise3(pPolygon);
        if (tClockwise) {
            tRes.push(pPolygon[0]);
            for (let i = pPolygon.length - 1; i > 0; i--) {
                tRes.push(pPolygon[i]);
            }
            return (tRes);
        }
        return (pPolygon);
    }
    static correctPolygonDirection2(pPolygon) {
        let tRes = pPolygon;
        let tClockwise = Util._clockWise2(tRes);
        if (tClockwise)
            tRes.reverse();
        return (tRes);
    }
    static polygon2CAG(pPoints) {
        let tRes = new CAG();
        let tPoints = new Array();
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < pPoints.length; i++) {
            tPoints.push(new CSG.Vector2D(pPoints[i].x, pPoints[i].z));
        }
        tRes = CAG.fromPointsNoCheck(tPoints);
        return (tRes);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    static line2D(pOptions, pScene) {
        //Arrays for vertex positions and indices
        let positions = [];
        let indices = [];
        let normals = [];
        let standardUV = [];
        let lineNormal = new Vector3(0, 0, 0);
        let direction = 0;
        let width = pOptions.width / 2 || 0.5;
        let path = pOptions.path;
        let closed = pOptions.closed || false;
        let rounded = pOptions.rounded || false;
        if (pOptions.standardUV === undefined) {
            standardUV = true;
        }
        else {
            standardUV = pOptions.standardUV;
        }
        let interiorIndex;
        //Arrays to hold wall corner data
        let innerBaseCorners = [];
        let outerBaseCorners = [];
        let outerData = [];
        let innerData = [];
        let angle = 0;
        let nbPoints = path.length;
        let line = Vector3.Zero();
        let nextLine = Vector3.Zero();
        path[1].subtractToRef(path[0], line);
        if (nbPoints > 2 && closed) {
            path[2].subtractToRef(path[1], nextLine);
            for (let p = 0; p < nbPoints; p++) {
                angle = Math.PI - Math.acos(Vector3.Dot(line, nextLine) / (line.length() * nextLine.length()));
                direction = Vector3.Cross(line, nextLine).normalize().z;
                lineNormal = new Vector3(line.y, -1 * line.x, 0).normalize();
                line.normalize();
                innerData[(p + 1) % nbPoints] = path[(p + 1) % nbPoints].subtract(lineNormal.scale(width)).subtract(line.scale(direction * width / Math.tan(angle / 2)));
                outerData[(p + 1) % nbPoints] = path[(p + 1) % nbPoints].add(lineNormal.scale(width)).add(line.scale(direction * width / Math.tan(angle / 2)));
                line = nextLine.clone();
                path[(p + 3) % nbPoints].subtractToRef(path[(p + 2) % nbPoints], nextLine);
            }
        }
        else {
            lineNormal = new Vector3(line.y, -1 * line.x, 0).normalize();
            line.normalize();
            innerData[0] = path[0].subtract(lineNormal.scale(width));
            outerData[0] = path[0].add(lineNormal.scale(width));
            for (let p = 0; p < nbPoints - 2; p++) {
                path[p + 2].subtractToRef(path[p + 1], nextLine);
                if (!rounded) {
                    angle = Math.PI - Math.acos(Vector3.Dot(line, nextLine) / (line.length() * nextLine.length()));
                }
                else {
                    angle = 3.141;
                }
                direction = Vector3.Cross(line, nextLine).normalize().z;
                lineNormal = new Vector3(line.y, -1 * line.x, 0).normalize();
                line.normalize();
                innerData[p + 1] = path[p + 1].subtract(lineNormal.scale(width)).subtract(line.scale(direction * width / Math.tan(angle / 2)));
                outerData[p + 1] = path[p + 1].add(lineNormal.scale(width)).add(line.scale(direction * width / Math.tan(angle / 2)));
                line = nextLine.clone();
            }
            if (nbPoints > 2) {
                path[nbPoints - 1].subtractToRef(path[nbPoints - 2], line);
                lineNormal = new Vector3(line.y, -1 * line.x, 0).normalize();
                line.normalize();
                innerData[nbPoints - 1] = path[nbPoints - 1].subtract(lineNormal.scale(width));
                outerData[nbPoints - 1] = path[nbPoints - 1].add(lineNormal.scale(width));
            }
            else {
                innerData[1] = path[1].subtract(lineNormal.scale(width));
                outerData[1] = path[1].add(lineNormal.scale(width));
            }
        }
        let maxX = Number.MIN_VALUE;
        let minX = Number.MAX_VALUE;
        let maxY = Number.MIN_VALUE;
        let minY = Number.MAX_VALUE;
        for (let p = 0; p < nbPoints; p++) {
            positions.push(innerData[p].x, innerData[p].y, innerData[p].z);
            maxX = Math.max(innerData[p].x, maxX);
            minX = Math.min(innerData[p].x, minX);
            maxY = Math.max(innerData[p].y, maxY);
            minY = Math.min(innerData[p].y, minY);
        }
        for (let p = 0; p < nbPoints; p++) {
            positions.push(outerData[p].x, outerData[p].y, outerData[p].z);
            maxX = Math.max(innerData[p].x, maxX);
            minX = Math.min(innerData[p].x, minX);
            maxY = Math.max(innerData[p].y, maxY);
            minY = Math.min(innerData[p].y, minY);
        }
        for (let i = 0; i < nbPoints - 1; i++) {
            indices.push(i, i + 1, nbPoints + i + 1);
            indices.push(i, nbPoints + i + 1, nbPoints + i);
        }
        if (nbPoints > 2 && closed) {
            indices.push(nbPoints - 1, 0, nbPoints);
            indices.push(nbPoints - 1, nbPoints, 2 * nbPoints - 1);
        }
        normals = [];
        let uvs = [];
        if (standardUV) {
            for (let p = 0; p < positions.length; p += 3) {
                uvs.push((positions[p] - minX) / (maxX - minX), (positions[p + 1] - minY) / (maxY - minY));
            }
        }
        else {
            let flip = 0;
            let p1 = 0;
            let p2 = 0;
            let p3 = 0;
            let v0 = innerData[0];
            let v1 = innerData[1].subtract(v0);
            let v2 = outerData[0].subtract(v0);
            let v3 = outerData[1].subtract(v0);
            let axis = v1.clone();
            axis.normalize();
            p1 = Vector3.Dot(axis, v1);
            p2 = Vector3.Dot(axis, v2);
            p3 = Vector3.Dot(axis, v3);
            let minX1 = Math.min(0, p1, p2, p3);
            let maxX1 = Math.max(0, p1, p2, p3);
            uvs[2 * indices[0]] = -minX1 / (maxX1 - minX1);
            uvs[2 * indices[0] + 1] = 1;
            uvs[2 * indices[5]] = (p2 - minX1) / (maxX1 - minX1);
            uvs[2 * indices[5] + 1] = 0;
            uvs[2 * indices[1]] = (p1 - minX1) / (maxX1 - minX1);
            uvs[2 * indices[1] + 1] = 1;
            uvs[2 * indices[4]] = (p3 - minX1) / (maxX1 - minX1);
            uvs[2 * indices[4] + 1] = 0;
            for (let i = 6; i < indices.length; i += 6) {
                flip = (flip + 1) % 2;
                v0 = innerData[0];
                v1 = innerData[1].subtract(v0);
                v2 = outerData[0].subtract(v0);
                v3 = outerData[1].subtract(v0);
                axis = v1.clone();
                axis.normalize();
                p1 = Vector3.Dot(axis, v1);
                p2 = Vector3.Dot(axis, v2);
                p3 = Vector3.Dot(axis, v3);
                let minX2 = Math.min(0, p1, p2, p3);
                let maxX2 = Math.max(0, p1, p2, p3);
                uvs[2 * indices[i + 1]] = flip + Math.cos(flip * Math.PI) * (p1 - minX2) / (maxX2 - minX2);
                uvs[2 * indices[i + 1] + 1] = 1;
                uvs[2 * indices[i + 4]] = flip + Math.cos(flip * Math.PI) * (p3 - minX2) / (maxX2 - minX2);
                uvs[2 * indices[i + 4] + 1] = 0;
            }
        }
        VertexData.ComputeNormals(positions, indices, normals);
        //Create a custom mesh
        let customMesh = new Mesh("custom", pScene);
        //Create a vertexData object
        let vertexData = new VertexData();
        //Assign positions and indices to vertexData
        vertexData.positions = positions;
        vertexData.indices = indices;
        vertexData.normals = normals;
        vertexData.uvs = uvs;
        //Apply vertexData to custom mesh
        vertexData.applyToMesh(customMesh);
        return customMesh;
    }
    static centerPolygon(pPolygon, pOffset = new Vector3(0, 0, 0)) {
        let tRes = [];
        if (pPolygon.length === 0)
            return (tRes);
        let tminX = pPolygon[0].x;
        let tminZ = pPolygon[0].z;
        let tmaxX = pPolygon[0].x;
        let tmaxZ = pPolygon[0].z;
        for (let tPoint of pPolygon) {
            tminX = Math.min(tPoint.x, tminX);
            tmaxX = Math.max(tPoint.x, tmaxX);
            tminZ = Math.min(tPoint.z, tminZ);
            tmaxZ = Math.max(tPoint.z, tmaxZ);
        }
        let tCenter = new Vector3();
        tCenter.x = tmaxX - (tmaxX - tminX) * 0.5;
        tCenter.z = tmaxZ - (tmaxZ - tminZ) * 0.5;
        let tnewPolygon = new Array();
        for (let tPoint of pPolygon) {
            let tnewPoint = tPoint.subtract(tCenter).subtract(pOffset);
            tnewPolygon.push(tnewPoint);
        }
        tRes = tnewPolygon;
        return (tRes);
    }
    static getCenterofPolygon(pPolygon) {
        let tRes = new Vector3(0, 0, 0);
        if (pPolygon.length === 0)
            return (tRes);
        let tpolygon = new Array();
        for (let tP of pPolygon)
            tpolygon.push(tP);
        if (!Util.vectorEquals3(tpolygon[0], tpolygon[tpolygon.length - 1]))
            tpolygon.push(tpolygon[0]);
        let tminX = tpolygon[0].x;
        let tminZ = tpolygon[0].z;
        let tmaxX = tpolygon[0].x;
        let tmaxZ = tpolygon[0].z;
        for (let tPoint of tpolygon) {
            tminX = Math.min(tPoint.x, tminX);
            tmaxX = Math.max(tPoint.x, tmaxX);
            tminZ = Math.min(tPoint.z, tminZ);
            tmaxZ = Math.max(tPoint.z, tmaxZ);
        }
        tRes.x = tmaxX - (tmaxX - tminX) * 0.5;
        tRes.z = tmaxZ - (tmaxZ - tminZ) * 0.5;
        return (tRes);
    }
    static getCenterofCAG(pCAG) {
        let tRes = new Vector3(0, 0, 0);
        if (pCAG.sides.length === 0)
            return (tRes);
        let tCAGbounds = pCAG.getBounds();
        let tminX = tCAGbounds[0].x;
        let tminZ = tCAGbounds[0].y;
        let tmaxX = tCAGbounds[1].x;
        let tmaxZ = tCAGbounds[1].y;
        tRes.x = tmaxX - (tmaxX - tminX) * 0.5;
        tRes.z = tmaxZ - (tmaxZ - tminZ) * 0.5;
        return (tRes);
    }
    static getWidthOfCAG(pCAG) {
        if (pCAG.sides.length === 0) {
            return (0);
        }
        let tCAGbounds = pCAG.getBounds();
        let tminX = tCAGbounds[0].x;
        let tmaxX = tCAGbounds[1].x;
        return (tmaxX - tminX);
    }
    static getDepthOfCAG(pCAG) {
        if (pCAG.sides.length === 0) {
            return (0);
        }
        let tCAGbounds = pCAG.getBounds();
        let tminZ = tCAGbounds[0].y;
        let tmaxZ = tCAGbounds[1].y;
        return (tmaxZ - tminZ);
    }
    static getBisector(pMiddlePoint, pLeftEnd, pRightEnd) {
        const tLeft = (pLeftEnd.subtract(pMiddlePoint)).normalize();
        const tRight = (pRightEnd.subtract(pMiddlePoint)).normalize();
        const tBiSector = (tLeft.add(tRight)).normalize();
        return (tBiSector);
    }
    static getBoundingBoxOfCAG(pCAG) {
        if (pCAG == null || pCAG.sides.length === 0) {
            return (null);
        }
        let tCAGbounds = pCAG.getBounds();
        let tminX = tCAGbounds[0].x;
        let tminZ = tCAGbounds[0].y;
        let tmaxX = tCAGbounds[1].x;
        let tmaxZ = tCAGbounds[1].y;
        const tBounds = new BoundingBox(new Vector3(tminX, 0.0, tminZ), new Vector3(tmaxX, 0, tmaxZ));
        return (tBounds);
    }
    static generateUUID() {
        return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
            let random = Math.random() * 16 | 0; // Nachkommastellen abschneiden
            let value = char === "x" ? random : (random % 4 + 8); // Bei x Random 0-15 (0-F), bei y Random 0-3 + 8 = 8-11 (8-b) gemäss RFC 4122
            return value.toString(16); // Hexadezimales Zeichen zurückgeben
        });
    }
    static generatePlaneUVS(pMesh, pMirror = false) {
        let vertexData = new VertexData();
        let indices = pMesh.getIndices();
        let positions = pMesh.getVerticesData(VertexBuffer.PositionKind);
        let uv = pMesh.getVerticesData(VertexBuffer.UVKind);
        vertexData.indices = indices;
        vertexData.positions = positions;
        if (uv != null) {
            let tP1 = new Vector2(uv[0], uv[1]);
            let tP2 = new Vector2(uv[2], uv[3]);
            let tDist = Vector2.Distance(tP1, tP2);
            if (!pMirror) {
                uv[0] = tDist;
                uv[1] = tP1.y;
                uv[2] = 0;
                uv[3] = tP1.y;
                uv[4] = tDist;
                uv[5] = 0;
                uv[6] = 0;
                uv[7] = 0;
                uv[8] = uv[0];
                uv[9] = uv[1];
                uv[10] = uv[2];
                uv[11] = uv[3];
                uv[12] = uv[4];
                uv[13] = uv[5];
                uv[14] = uv[6];
                uv[16] = uv[7];
            }
            else {
                uv[0] = tDist;
                uv[1] = tP1.y;
                uv[2] = 0;
                uv[3] = tP1.y;
                uv[4] = tDist;
                uv[5] = 0;
                uv[6] = 0;
                uv[7] = 0;
                uv[8] = uv[2];
                uv[9] = uv[3];
                uv[10] = uv[0];
                uv[11] = uv[1];
                uv[12] = uv[6];
                uv[13] = uv[7];
                uv[14] = uv[4];
                uv[16] = uv[5];
            }
            vertexData.uvs = uv;
        }
        vertexData.applyToMesh(pMesh);
        return (pMesh);
    }
    static CAG2CSG(pCAG, pExtrudeHeight = 0) {
        let tRes = new CSG();
        if (pExtrudeHeight !== 0) {
            tRes = pCAG.extrude({ offset: [0, 0, pExtrudeHeight] });
            tRes = tRes.rotate(new CSG.Vector3D([0, 0, 0]), new CSG.Vector3D([1, 0, 0]), 90);
            tRes = tRes.translate(new CSG.Vector3D([0, pExtrudeHeight, 0]));
            return (tRes);
        }
        if (pCAG.sides.length > 0) {
            let tcsgPolygons = pCAG._toPlanePolygons({
                translation: [0, 0, [0]],
                normalVector: CSG.Vector3D.Create(0, 1, 0),
                flipped: true
            });
            for (let tPoly of tcsgPolygons) {
                tRes.polygons.push(tPoly);
            }
            tRes = tRes.rotateX(90);
        }
        return (tRes);
    }
    static drawLine(p2dPoints, pScene) {
        let tLines = CreateLines("", { points: p2dPoints }, pScene);
        tLines.color = Color3.FromHexString("#FF0000");
        ShadowGenerator.disableMeshShadow(tLines);
        if (tLines != null)
            return (tLines);
        return (null);
    }
    static getHashOfString(pString) {
        let thash = 0;
        let tchr = 0;
        if (pString.length === 0)
            return thash.toString();
        for (let i = 0; i < pString.length; i++) {
            tchr = pString.charCodeAt(i);
            thash = ((thash << 5) - thash) + tchr;
            thash |= 0; // Convert to 32bit integer
        }
        thash = Math.abs(thash);
        return (thash.toString());
    }
    static hexToRGB(pHexColorString) {
        let tRes = {};
        let tshortColorRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
        // tslint:disable-next-line:only-arrow-functions
        let thexValue = pHexColorString.replace(tshortColorRegex, function (m, r, g, b) {
            return r + r + g + g + b + b;
        });
        let tresultValue = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(thexValue);
        let tresString = "";
        if (tresultValue != null) {
            tRes.r = parseInt(tresultValue[1], 16) / 255;
            tRes.g = parseInt(tresultValue[2], 16) / 255;
            tRes.b = parseInt(tresultValue[3], 16) / 255;
        }
        return (tRes);
    }
    static to2d(pV) {
        return (new Vector2(pV.x, pV.z));
    }
    static to2dArray(p3dPointArray) {
        let tRes = new Array();
        for (let tP of p3dPointArray) {
            let t2d = new Vector2(0, 0);
            t2d.x = tP.x;
            t2d.y = tP.z;
            tRes.push(t2d);
        }
        return (tRes);
    }
    static to3dArray(p2dPointArray) {
        let tRes = new Array();
        for (let tP of p2dPointArray) {
            let t3d = new Vector3(0, 0, 0);
            t3d.x = tP.x;
            t3d.z = tP.y;
            t3d.y = 0;
            tRes.push(t3d);
        }
        return (tRes);
    }
    static to3d(pV) {
        return (new Vector3(pV.x, 0.0, pV.y));
    }
    static invert2d(pV) {
        return (new Vector2(pV.x * -1, pV.y * -1));
    }
    /**
     * Checks, whether 2 points (vector3) are the same
     * including a delta to check
     */
    static samePointsV3(pP1, pP2, pDelta) {
        let result = false;
        let tdiffX = Util.diff(pP1.x, pP2.x);
        let tdiffZ = Util.diff(pP1.z, pP2.z);
        if (tdiffX <= pDelta && tdiffZ <= pDelta)
            result = true;
        return result;
    }
    /**
     * Checks, whether 2 points (vector2) are the same
     * including a delta to check
     */
    static samePointsV2(pP1, pP2, pDelta) {
        let result = false;
        let tdiffX = Util.diff(pP1.x, pP2.x);
        let tdiffZ = Util.diff(pP1.y, pP2.y);
        if (tdiffX < pDelta && tdiffZ < pDelta)
            result = true;
        return result;
    }
    /**
     * Length difference of 2 values/points
     */
    static diff(pNumber1, pNumber2) {
        return Math.abs(pNumber1 - pNumber2);
    }
    static center2d(pV1, pV2) {
        let tVmid = new Vector2(0, 0);
        tVmid.x = (pV1.x + pV2.x) * 0.5;
        tVmid.y = (pV1.y + pV2.y) * 0.5;
        return (tVmid);
    }
    static center3d(pV1, pV2) {
        let tVmid = new Vector3(0, 0, 0);
        tVmid.x = (pV1.x + pV2.x) * 0.5;
        tVmid.y = (pV1.y + pV2.y) * 0.5;
        tVmid.z = (pV1.z + pV2.z) * 0.5;
        return (tVmid);
    }
    static createTextPlane(pCoreApp, pText, pFontSize, pPlaneHeight, pTextColor) {
        //Set font
        let tFont = "bold " + pFontSize.toString() + 'px ' + pCoreApp.theme.fontFamily;
        //Set height for dynamic texture
        const tDTHeight = 1 * pFontSize; //or set as wished
        //Calcultae ratio
        const tratio = pPlaneHeight / tDTHeight;
        //Use a temporay dynamic texture to calculate the length of the text on the dynamic texture canvas
        const ttmpDT = new DynamicTexture("DynamicTexture", 64, pCoreApp.scene);
        const tmpctx = ttmpDT.getContext();
        tmpctx.font = tFont;
        const tDTWidth = tmpctx.measureText(pText).width + 8;
        ttmpDT.dispose();
        //Calculate width the plane has to be
        const tplaneWidth = tDTWidth * tratio;
        //Create dynamic texture and write the text
        const tDT = new DynamicTexture("DynamicTexture", { width: tDTWidth, height: tDTHeight }, pCoreApp.scene, false);
        tDT.hasAlpha = true;
        const tmat = new StandardMaterial("mat", pCoreApp.scene);
        tmat.disableLighting = true;
        tmat.specularColor = Color3.Black();
        tmat.emissiveTexture = tDT;
        tmat.opacityTexture = tDT;
        tmat.backFaceCulling = false;
        tDT.drawText(pText, null, null, tFont, pTextColor, "transparent", true);
        //Create plane and set dynamic texture as material
        const tplane = CreatePlane("plane", { width: tplaneWidth, height: pPlaneHeight }, pCoreApp.scene);
        tplane.rotation.x = Math.PI / 2;
        tplane.material = tmat;
        const tscale = 1;
        tplane.scaling = new Vector3(tscale, tscale, tscale);
        return (tplane);
    }
    static vectorEquals2(pV1, pV2, pEpsilon = EPSILON) {
        return (pV1.equalsWithEpsilon(pV2, pEpsilon));
    }
    static vectorEquals3(pV1, pV2, pEpsilon = EPSILON) {
        return (pV1.equalsWithEpsilon(pV2, pEpsilon));
    }
    static numbersEqual(pNumber1, pNumber2, pEpsilon = EPSILON) {
        let tdiff = Math.abs(pNumber1 - pNumber2);
        let tRes = tdiff < pEpsilon ? true : false;
        return (tRes);
    }
    static rotateVector(pVector, pDegrees) {
        let sin = Math.sin(pDegrees * Math.PI / 180);
        let cos = Math.cos(pDegrees * Math.PI / 180);
        let tx = pVector.x;
        let ty = pVector.y;
        pVector.x = (cos * tx) - (sin * ty);
        pVector.y = (sin * tx) + (cos * ty);
        return pVector;
    }
    static rotateVector_V3(pVector, pDegrees) {
        let sin = Math.sin(pDegrees * Math.PI / 180);
        let cos = Math.cos(pDegrees * Math.PI / 180);
        let tx = pVector.x;
        let ty = pVector.y;
        pVector.x = (cos * tx) - (sin * ty);
        pVector.y = (sin * tx) + (cos * ty);
        return pVector;
    }
    static closedPolygon(pPoints) {
        let tRes = false;
        if (pPoints.length < 3)
            return (false);
        return (Util.vectorEquals3(pPoints[0], pPoints[pPoints.length - 1]));
    }
    static clockwise(pPoints) {
        let tSum = 0;
        let tLength = pPoints.length - 1;
        if (Util.closedPolygon(pPoints))
            tLength = pPoints.length - 2;
        for (let i = 0; i < tLength; i++) {
            let t_X = (pPoints[i + 1].x) - (pPoints[i].x);
            let t_Y = (pPoints[i + 1].z) - (pPoints[i].z);
            tSum += t_X * t_Y;
        }
        let tX = (pPoints[0].x) - (pPoints[tLength].x);
        let tY = (pPoints[0].z) - (pPoints[tLength].z);
        tSum += tX * tY;
        if (tSum > 0)
            return true;
        return false;
    }
    static _clockWise3(pPoints) {
        let tPoints = pPoints;
        if (tPoints.length < 3)
            return (false);
        let tArea = 0;
        let j = tPoints.length - 1;
        for (let i = 0; i < tPoints.length; ++i) {
            tArea += ((tPoints[j].x * tPoints[i].z) - (tPoints[j].z * tPoints[i].x));
            j = i;
        }
        return (geq(tArea * 0.5, 0) ? false : true);
    }
    static _clockWise2(pPoints) {
        let tPoints = pPoints;
        if (tPoints.length < 3)
            return (false);
        let tArea = 0;
        let j = tPoints.length - 1;
        for (let i = 0; i < tPoints.length; ++i) {
            tArea += ((tPoints[j].x * tPoints[i].y) - (tPoints[j].y * tPoints[i].x));
            j = i;
        }
        return (geq(tArea * 0.5, 0) ? false : true);
    }
    static _clockWise2d(pPoints) {
        let tPoints = pPoints;
        if (tPoints.length < 3)
            return (false);
        let tArea = 0;
        let j = tPoints.length - 1;
        for (let i = 0; i < tPoints.length; ++i) {
            tArea += ((tPoints[j].x * tPoints[i].y) - (tPoints[j].y * tPoints[i].x));
            j = i;
        }
        return (geq(tArea * 0.5, 0) ? false : true);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    static pointInQuadix(pP, pP1, pP2, pP3, pP4) {
        let tOr1 = orientationToLine2(pP, pP1, pP2);
        let tOr2 = orientationToLine2(pP, pP2, pP3);
        let tOr3 = orientationToLine2(pP, pP3, pP4);
        let tOr4 = orientationToLine2(pP, pP4, pP1);
        if ((tOr1 === tOr2) && (tOr2 === tOr3) && (tOr3 === tOr4))
            return (true);
        if (tOr1 === Orientation.Collinear)
            return (((tOr2 * tOr4) === Orientation.Collinear) || (tOr2 === tOr4));
        if (tOr2 === Orientation.Collinear)
            return (((tOr1 * tOr3) === Orientation.Collinear) || (tOr1 === tOr3));
        if (tOr3 === Orientation.Collinear)
            return (((tOr2 * tOr4) === Orientation.Collinear) || (tOr2 === tOr4));
        if (tOr4 === Orientation.Collinear)
            return (((tOr1 * tOr3) === Orientation.Collinear) || (tOr1 === tOr3));
        return (false);
    }
    static array_move(pArray, pOld_index, pNew_index) {
        if (pNew_index >= pArray.length) {
            let k = pNew_index - pArray.length + 1;
            while (k--) {
                pArray.push(undefined);
            }
        }
        pArray.splice(pNew_index, 0, pArray.splice(pOld_index, 1)[0]);
    }
    static arrayContainsSamePoints(pPoints) {
        let tRes = false;
        for (let i = 0; i < pPoints.length - 1; i++) {
            for (let j = i + 1; j < pPoints.length; j++) {
                const tP0 = pPoints[i];
                const tP1 = pPoints[j];
                if (this.vectorEquals3(tP0, tP1)) {
                    if (Math.abs(i - j) == 1) {
                        tRes = true;
                        break;
                    }
                }
            }
        }
        return (tRes);
    }
    static averagePointDistToZero(pPoints) {
        if (pPoints.length === 0) {
            return (0);
        }
        let tRes = 0;
        for (let tP of pPoints) {
            tRes += Vector3.Distance(Vector3.Zero(), tP);
        }
        tRes = tRes / pPoints.length;
        return (tRes);
    }
    static pointOnQuadix(pP, pP1, pP2, pP3, pP4) {
        return isPointOnSegment2(pP, pP1, pP2) ||
            isPointOnSegment2(pP, pP2, pP3) ||
            isPointOnSegment2(pP, pP3, pP4) ||
            isPointOnSegment2(pP, pP4, pP1);
    }
    static calcMeshArea(pMesh) {
        let tArea = 0;
        if (pMesh == null)
            return (tArea);
        let indices = pMesh.getIndices();
        let positions = pMesh.getVerticesData(VertexBuffer.PositionKind);
        if (indices != null && positions != null) {
            let v1x = 0.0;
            let v1y = 0.0;
            let v1z = 0.0;
            let v2x = 0.0;
            let v2y = 0.0;
            let v2z = 0.0;
            let crossx = 0.0;
            let crossy = 0.0;
            let crossz = 0.0;
            let i1 = 0;
            let i2 = 0;
            let i3 = 0;
            let nbFaces = indices.length / 3;
            for (var i = 0; i < nbFaces; i++) {
                i1 = indices[i * 3];
                i2 = indices[i * 3 + 1];
                i3 = indices[i * 3 + 2];
                v1x = positions[i1 * 3] - positions[i2 * 3];
                v1y = positions[i1 * 3 + 1] - positions[i2 * 3 + 1];
                v1z = positions[i1 * 3 + 2] - positions[i2 * 3 + 2];
                v2x = positions[i3 * 3] - positions[i2 * 3];
                v2y = positions[i3 * 3 + 1] - positions[i2 * 3 + 1];
                v2z = positions[i3 * 3 + 2] - positions[i2 * 3 + 2];
                crossx = v1y * v2z - v1z * v2y;
                crossy = v1z * v2x - v1x * v2z;
                crossz = v1x * v2y - v1y * v2x;
                tArea = tArea + Math.sqrt(crossx * crossx + crossy * crossy + crossz * crossz);
            }
        }
        return (tArea);
    }
    static getCAGPerimeter(pCAG) {
        let tRes = 0;
        if (pCAG.sides.length === 0)
            return (tRes);
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < pCAG.sides.length; i++) {
            let tV0 = pCAG.sides[i].vertex0;
            let tV1 = pCAG.sides[i].vertex1;
            let tP0 = new Vector2(tV0.pos.x, tV0.pos.y);
            let tP1 = new Vector2(tV1.pos.x, tV1.pos.y);
            tRes += Vector2.Distance(tP0, tP1);
        }
        return (tRes);
    }
    static getLineDistance(pLines) {
        let tRes = 0;
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < pLines.length; i++) {
            if (pLines[i] != null)
                tRes += Vector3.Distance(pLines[i][0], pLines[i][1]);
        }
        return (tRes);
    }
    static getPerimeterOfClosedPolygon(pPolyPoints) {
        let tRes = 0;
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < pPolyPoints.length - 1; i++) {
            const tP0 = pPolyPoints[i];
            const tP1 = pPolyPoints[i + 1];
            if (tP0 != null && tP1 != null) {
                tRes += Vector3.Distance(tP0, tP1);
            }
        }
        const tPF = pPolyPoints[0];
        const tPL = pPolyPoints[pPolyPoints.length - 1];
        if (tPF != null && tPL != null) {
            tRes += Vector3.Distance(tPF, tPL);
        }
        return (tRes);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    static getPerimeterLines(pMesh) {
        let tRes = new Array();
        if (pMesh == null)
            return (tRes);
        let tWM = pMesh.getWorldMatrix();
        let tindices = pMesh.getIndices();
        let tpdata = pMesh.getVerticesData(VertexBuffer.PositionKind);
        let tndata = pMesh.getVerticesData(VertexBuffer.NormalKind);
        if (tpdata == null || tindices == null || tndata == null)
            return (tRes);
        // get outside facet (normal: y = 0)
        for (var i = 0; i < tindices.length; i += 3) {
            let tfIdx1 = tindices[i] * 3;
            let tfIdx2 = tindices[i + 1] * 3;
            let tfIdx3 = tindices[i + 2] * 3;
            let tP1 = Vector3.FromArray(tpdata, tfIdx1);
            let tP2 = Vector3.FromArray(tpdata, tfIdx2);
            let tP3 = Vector3.FromArray(tpdata, tfIdx3);
            let tnP1 = Vector3.FromArray(tndata, tfIdx1);
            let tnP2 = Vector3.FromArray(tndata, tfIdx2);
            let tnP3 = Vector3.FromArray(tndata, tfIdx3);
            tP1 = Vector3.TransformCoordinates(tP1, tWM);
            tP2 = Vector3.TransformCoordinates(tP2, tWM);
            tP3 = Vector3.TransformCoordinates(tP3, tWM);
            if ((tnP1.normalize().y !== 1 && tnP2.normalize().y !== 1 && tnP3.normalize().y !== 1) &&
                (tnP1.normalize().y !== -1 && tnP2.normalize().y !== -1 && tnP3.normalize().y !== -1) &&
                ((tP1.y === tP2.y) || (tP2.y === tP3.y) || (tP1.y === tP3.y))) {
                if ((tP1.y === tP2.y) && (tP1.y > -0.0005)) {
                    tRes.push([tP1, tP2]);
                }
                if ((tP2.y === tP3.y) && (tP2.y > -0.0005)) {
                    tRes.push([tP2, tP3]);
                }
                if ((tP1.y === tP3.y) && (tP1.y > -0.0005)) {
                    tRes.push([tP1, tP3]);
                }
            }
        }
        for (let tLine of tRes) {
            tLine[0].y = 0;
            tLine[1].y = 0;
        }
        return (tRes);
    }
    static getPickPlane(pView, pMoveY = false, pRefPoint) {
        let tCamMode = pView.cameraControl.cameraMode;
        if (tCamMode === CameraMode.Orthographic ||
            tCamMode === CameraMode.Perspective) {
            let tNormal = null;
            if (pMoveY) {
                let tCamDir = pView.cameraControl.direction.negate().normalize();
                tCamDir.y = 0;
                tNormal = tCamDir;
                // tNormal = wcfMath.VECTOR3_X;
                // let tDot = Math.abs(Vector3.Dot(tCamDir, tNormal));
                // let tNewNormal = wcfMath.VECTOR3_Z;
                // let tNewDot = Math.abs(Vector3.Dot(tCamDir, tNewNormal));
                // if (tNewDot > tDot)
                //     tNormal = tNewNormal;
            }
            else {
                tNormal = VECTOR3_Y;
                //switch from XZ plane to plane defined by camera for snapped side views
                if (tCamMode === CameraMode.Orthographic &&
                    pView.cameraControl.orthoOrbitSnappingEnabled) {
                    let tCamDir = pView.cameraControl.direction;
                    let tDot = Vector3.Dot(tCamDir, tNormal);
                    if (eq(tDot, 0.0)) {
                        tNormal = tCamDir;
                    }
                }
            }
            return Plane.FromPositionAndNormal(pRefPoint, tNormal);
        }
        return Plane.FromPositionAndNormal(pRefPoint, pView.cameraControl.direction.negate().normalize());
    }
    static getScaleFactor(pRoomManager, pMesh = null, pScaleFactor = 10) {
        let tScaleFactor = null;
        if (pRoomManager.hoverView == null || pMesh == null)
            return (tScaleFactor);
        tScaleFactor = Util.getViewScaleFactorOfNode(pRoomManager.hoverView, pMesh, 0.1, pScaleFactor);
        return (tScaleFactor);
    }
    static getSortedLine(pP1, pP2) {
        let tRes = new Array();
        if (pP1.x < pP2.x) {
            tRes.push(pP1, pP2);
        }
        else if (pP1.x === pP2.x) {
            if (pP1.z < pP2.z) {
                tRes.push(pP1, pP2);
            }
            else {
                tRes.push(pP2, pP1);
            }
        }
        else if (pP1.x > pP2.x) {
            tRes.push(pP2, pP1);
        }
        return (tRes);
    }
    static fixDown(pValue, pDigits) {
        let re = new RegExp("(\\d+\\.\\d{" + pDigits + "})(\\d)");
        let m = pValue.toString().match(re);
        if (m != null) {
            let tRes = parseFloat(m[1]);
            return (tRes);
        }
        return (pValue);
    }
    static getUniqueMeshLines(pMesh) {
        let tLines = new Array();
        let tRes = new Array();
        let tVPos = pMesh.getVerticesData(VertexBuffer.PositionKind);
        let tVIdx = pMesh.getIndices();
        if (tVPos == null || tVIdx == null)
            return (tRes);
        let tuniqueLinesStr = new Array();
        let tuniqueLines = new Array();
        // collect lines for only upper side (y=0) of mesh (2d shape)
        for (let i = 0; i < tVIdx.length; i += 3) {
            let tI1 = tVIdx[i];
            let tI2 = tVIdx[i + 1];
            let tI3 = tVIdx[i + 2];
            let tDelta = 1000000000000;
            let tP1x = Math.floor(tVPos[tI1 * 3] * tDelta) / tDelta;
            let tP1y = Math.floor(tVPos[tI1 * 3 + 1] * tDelta) / tDelta;
            let tP1z = Math.floor(tVPos[tI1 * 3 + 2] * tDelta) / tDelta;
            let tP2x = Math.floor(tVPos[tI2 * 3] * tDelta) / tDelta;
            let tP2y = Math.floor(tVPos[tI2 * 3 + 1] * tDelta) / tDelta;
            let tP2z = Math.floor(tVPos[tI2 * 3 + 2] * tDelta) / tDelta;
            let tP3x = Math.floor(tVPos[tI3 * 3] * tDelta) / tDelta;
            let tP3y = Math.floor(tVPos[tI3 * 3 + 1] * tDelta) / tDelta;
            let tP3z = Math.floor(tVPos[tI3 * 3 + 2] * tDelta) / tDelta;
            let tP1 = new Vector3(tP1x, tP1y, tP1z);
            let tP2 = new Vector3(tP2x, tP2y, tP2z);
            let tP3 = new Vector3(tP3x, tP3y, tP3z);
            let tLine = new Array();
            if ((tP1.y === 0) && (tP2.y === 0) && (tP3.y === 0)) {
                let tIdx = 0;
                tLine = Util.getSortedLine(tP1, tP2);
                tIdx = tuniqueLinesStr.indexOf(tLine.toString());
                if (tIdx === -1) {
                    tuniqueLinesStr.push(tLine.toString());
                    tuniqueLines.push(tLine);
                }
                else {
                    tuniqueLinesStr.splice(tIdx, 1);
                    tuniqueLines.splice(tIdx, 1);
                }
                tLine = Util.getSortedLine(tP2, tP3);
                tIdx = tuniqueLinesStr.indexOf(tLine.toString());
                if (tuniqueLinesStr.indexOf(tLine.toString()) === -1) {
                    tuniqueLinesStr.push(tLine.toString());
                    tuniqueLines.push(tLine);
                }
                else {
                    tuniqueLinesStr.splice(tIdx, 1);
                    tuniqueLines.splice(tIdx, 1);
                }
                tLine = Util.getSortedLine(tP3, tP1);
                tIdx = tuniqueLinesStr.indexOf(tLine.toString());
                if (tuniqueLinesStr.indexOf(tLine.toString()) === -1) {
                    tuniqueLinesStr.push(tLine.toString());
                    tuniqueLines.push(tLine);
                }
                else {
                    tuniqueLinesStr.splice(tIdx, 1);
                    tuniqueLines.splice(tIdx, 1);
                }
            }
        }
        tRes = tuniqueLines;
        return (tRes);
    }
    static getMeshVertices(pMesh, pDelta) {
        let tRes = new Array();
        let tVPos = pMesh.getVerticesData(VertexBuffer.PositionKind);
        let tVIdx = pMesh.getIndices();
        let tuniqueLinesStr = new Array();
        let tuniqueLines = new Array();
        let tallStrings = new Array();
        if (tVPos == null || tVIdx == null)
            return (tRes);
        for (let i = 0; i < tVIdx.length; i += 3) {
            let tI1 = tVIdx[i];
            let tI2 = tVIdx[i + 1];
            let tI3 = tVIdx[i + 2];
            let tDelta = pDelta;
            let tX = Math.floor(tVPos[tI1 * 3] * tDelta) / tDelta;
            let tY = Math.floor(tVPos[tI1 * 3 + 1] * tDelta) / tDelta;
            let tZ = Math.floor(tVPos[tI1 * 3 + 2] * tDelta) / tDelta;
            let thP1 = new Vector3(tX, tY, tZ);
            let tX1 = Math.floor(tVPos[tI2 * 3] * tDelta) / tDelta;
            let tY1 = Math.floor(tVPos[tI2 * 3 + 1] * tDelta) / tDelta;
            let tZ1 = Math.floor(tVPos[tI2 * 3 + 2] * tDelta) / tDelta;
            let thP2 = new Vector3(tX1, tY1, tZ1);
            let tX2 = Math.floor(tVPos[tI3 * 3] * tDelta) / tDelta;
            let tY2 = Math.floor(tVPos[tI3 * 3 + 1] * tDelta) / tDelta;
            let tZ2 = Math.floor(tVPos[tI3 * 3 + 2] * tDelta) / tDelta;
            let thP3 = new Vector3(tX2, tY2, tZ2);
            if ((thP1.y === 0) && (thP2.y === 0) && (thP3.y === 0)) {
                // let tP1 = Util.to2d(thP1);
                // let tP2 = Util.to2d(thP2);
                // let tP3 = Util.to2d(thP3);
                let tP1 = thP1;
                let tP2 = thP2;
                let tP3 = thP3;
                let tLineStr1 = [tP1, tP2].toString();
                let tLineStr11 = [tP2, tP1].toString();
                tallStrings.push(tLineStr1);
                tallStrings.push(tLineStr11);
                tallStrings.push("###");
                let tIdx1 = tuniqueLinesStr.findIndex(element => element.includes(tLineStr1));
                if (tIdx1 === -1) {
                    tuniqueLinesStr.push(tLineStr1 + "," + tLineStr11);
                    tuniqueLines.push([tP1, tP2]);
                    tRes.push([tP1, tP2]);
                }
                else {
                    tuniqueLinesStr.splice(tIdx1, 1);
                    tuniqueLines.splice(tIdx1, 1);
                    tRes.splice(tIdx1, 1);
                }
                let tLineStr2 = [tP2, tP3].toString();
                let tLineStr21 = [tP3, tP2].toString();
                tallStrings.push(tLineStr2);
                tallStrings.push(tLineStr21);
                tallStrings.push("###");
                let tIdx2 = tuniqueLinesStr.findIndex(element => element.includes(tLineStr2));
                if (tIdx2 === -1) {
                    tuniqueLinesStr.push(tLineStr2 + "," + tLineStr21);
                    tuniqueLines.push([tP2, tP3]);
                    tRes.push([tP2, tP3]);
                }
                else {
                    tuniqueLinesStr.splice(tIdx2, 1);
                    tuniqueLines.splice(tIdx2, 1);
                    tRes.splice(tIdx2, 1);
                }
                let tLineStr3 = [tP3, tP1].toString();
                let tLineStr31 = [tP1, tP3].toString();
                tallStrings.push(tLineStr3);
                tallStrings.push(tLineStr31);
                tallStrings.push("###");
                let tIdx3 = tuniqueLinesStr.findIndex(element => element.includes(tLineStr3));
                if (tIdx3 === -1) {
                    tuniqueLinesStr.push(tLineStr3 + "," + tLineStr31);
                    tuniqueLines.push([tP3, tP1]);
                    tRes.push([tP3, tP1]);
                }
                else {
                    tuniqueLinesStr.splice(tIdx3, 1);
                    tuniqueLines.splice(tIdx3, 1);
                    tRes.splice(tIdx3, 1);
                }
            }
        }
        let blob = new Blob([tallStrings.toString()], {
            type: "text/xml"
        });
        let a = document.createElement("a");
        a.href = window.URL.createObjectURL(blob);
        a.download = "all.txt";
        a.click();
        let blob1 = new Blob([tuniqueLinesStr.toString()], {
            type: "text/xml"
        });
        let a1 = document.createElement("a");
        a1.href = window.URL.createObjectURL(blob1);
        a1.download = "unique.txt";
        a1.click();
        return (tRes);
    }
    static vecToLocal(pVector, pMesh) {
        let m = pMesh.getWorldMatrix();
        let v = Vector3.TransformCoordinates(pVector, m);
        return (v);
    }
    static Filter(pMesh) {
        return true;
    }
    static castRay(pMesh, patHeight, pAngle, pScene) {
        let tBounds = pMesh.getBoundingInfo();
        let origin = new Vector3(tBounds.boundingBox.centerWorld.x, patHeight, tBounds.boundingBox.centerWorld.z);
        let forward = new Vector3(Math.cos(pAngle), 0, Math.sin(pAngle));
        forward = this.vecToLocal(forward, pMesh);
        let length = 1000;
        let ray = new Ray(origin, forward, length);
        //let rayHelper = new RayHelper(ray);
        //rayHelper.show(pScene, Color3.Black());
        let hit = pScene.pickWithRay(ray, this.Filter);
        if (hit != null) {
            if (hit.pickedPoint != null)
                return (hit.pickedPoint);
            return (new Vector3(0, 0, 0));
        }
        return (new Vector3(0, 0, 0));
    }
    static calcMeshPerimeterByRay(pMesh, patHeight, pIncrements, pScene) {
        let current = this.castRay(pMesh, patHeight, 0, pScene);
        let next = Vector3.Zero();
        let circumference = 0;
        let deltaCirc = 0;
        for (var theta = pIncrements; theta <= 2 * Math.PI; theta += pIncrements) {
            next = this.castRay(pMesh, patHeight, theta, pScene);
            deltaCirc = next.subtract(current).length();
            circumference += deltaCirc;
            current = new Vector3(next.x, next.y, next.z);
        }
        return (circumference);
    }
    static calcMeshPerimeterByLines(pLines) {
        let tRes = 0;
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < pLines.length; i++) {
            tRes += Vector3.Distance(pLines[i].Start, pLines[i].End);
        }
        return (tRes);
    }
    static calcMeshPerimeterByPoints(pPolygon) {
        let tRes = 0;
        if (pPolygon.length < 2)
            return (tRes);
        for (let i = 0; i < pPolygon.length - 1; i++) {
            tRes += Vector2.Distance(pPolygon[i], pPolygon[i + 1]);
        }
        tRes += Vector2.Distance(pPolygon[pPolygon.length - 1], pPolygon[0]);
        return (tRes);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    static getPerimeterLineOfMesh(pMesh) {
        let tRes = new Array();
        if (pMesh == null)
            return (tRes);
        let tmIdx = pMesh.getIndices();
        let tpdata = pMesh.getVerticesData(VertexBuffer.PositionKind);
        if (tpdata == null)
            return (tRes);
        let tipData = [];
        //handle possible scaling
        for (let i = 0; i < tpdata.length; i += 3) {
            let t1 = tpdata[i] * pMesh.scaling.x;
            let t2 = tpdata[i + 1] * pMesh.scaling.y;
            let t3 = tpdata[i + 2] * pMesh.scaling.z;
            tipData.push(t1);
            tipData.push(t2);
            tipData.push(t3);
        }
        tRes = [];
        // get outside facet (normal: y =0)
        let tfn = [];
        pMesh.updateFacetData();
        if (tmIdx == null)
            return (tRes);
        for (let i = 0; i < pMesh.facetNb; i++) {
            let tn = pMesh.getFacetNormal(i);
            if ((tn.y < 0.001) && (tn.y > -0.001)) {
                //if (tn.y == 0) {
                tfn.push(i);
            }
        }
        // tslint:disable-next-line:prefer-for-of
        for (var p = 0; p < tfn.length; p++) {
            let tLine = [];
            let tpStart = tfn[p] * 3;
            let tfIdx1 = tmIdx[tpStart] * 3;
            let tfIdx2 = tmIdx[tpStart + 1] * 3;
            let tfIdx3 = tmIdx[tpStart + 2] * 3;
            let tP1 = Vector3.FromArray(tipData, tfIdx1);
            let tP2 = Vector3.FromArray(tipData, tfIdx2);
            let tP3 = Vector3.FromArray(tipData, tfIdx3);
            let tL = [];
            if (tP1.y === -0.001) {
                tP1.y = 0;
                tL.push(tP1);
            }
            if (tP2.y === -0.001) {
                tP2.y = 0;
                tL.push(tP2);
            }
            if (tP3.y === -0.001) {
                tP3.y = 0;
                tL.push(tP3);
            }
            if (tL.length > 1) {
                tRes.push(tL);
            }
        }
        return (tRes);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    static convertMeshPlane2CAG(pMesh) {
        let tRes = null;
        if (pMesh == null) {
            return (tRes);
        }
        const tindices = pMesh.getIndices();
        const tpdata = pMesh.getVerticesData(VertexBuffer.PositionKind);
        if (tpdata == null || tindices == null) {
            return (tRes);
        }
        const tWM = pMesh.computeWorldMatrix();
        let tipData = [];
        //handle possible scaling
        for (let i = 0; i < tpdata.length; i += 3) {
            let tScaling = new Vector3(1, 1, 1);
            // tScaling = pMesh.scaling.clone();
            let t1 = tpdata[i] * tScaling.x;
            let t2 = tpdata[i + 1] * tScaling.y;
            let t3 = tpdata[i + 2] * tScaling.z;
            tipData.push(t1);
            tipData.push(t2);
            tipData.push(t3);
        }
        let tfn = [];
        pMesh.updateFacetData();
        for (let i = 0; i < pMesh.facetNb; i++) {
            let tn = pMesh.getFacetNormal(i);
            tn = tn.normalize();
            if (Util.numbersEqual(tn.y, 1, 0.00001)) {
                tfn.push(i);
            }
        }
        let tCAGarray = new Array();
        let taimHeight;
        // tslint:disable-next-line:prefer-for-of
        for (var p = 0; p < tfn.length; p++) {
            let tpStart = tfn[p] * 3;
            let tfIdx1 = tindices[tpStart] * 3;
            let tfIdx2 = tindices[tpStart + 1] * 3;
            let tfIdx3 = tindices[tpStart + 2] * 3;
            let tP1 = Vector3.FromArray(tipData, tfIdx1);
            let tP2 = Vector3.FromArray(tipData, tfIdx2);
            let tP3 = Vector3.FromArray(tipData, tfIdx3);
            tP1 = Vector3.TransformCoordinates(tP1, tWM);
            tP2 = Vector3.TransformCoordinates(tP2, tWM);
            tP3 = Vector3.TransformCoordinates(tP3, tWM);
            const tPoints = new Array();
            if (taimHeight === undefined) {
                if (Util.numbersEqual(tP1.y, tP2.y) && Util.numbersEqual(tP2.y, tP3.y)) {
                    taimHeight = tP1.y;
                    tPoints.push(new CSG.Vector2D(tP1.x, tP1.z));
                    tPoints.push(new CSG.Vector2D(tP2.x, tP2.z));
                    tPoints.push(new CSG.Vector2D(tP3.x, tP3.z));
                }
            }
            else if (Util.numbersEqual(tP1.y, taimHeight) &&
                Util.numbersEqual(tP2.y, taimHeight) &&
                Util.numbersEqual(tP3.y, taimHeight)) {
                tPoints.push(new CSG.Vector2D(tP1.x, tP1.z));
                tPoints.push(new CSG.Vector2D(tP2.x, tP2.z));
                tPoints.push(new CSG.Vector2D(tP3.x, tP3.z));
            }
            let tCAGshape = null;
            try {
                tCAGshape = CAG.fromPoints(tPoints);
            }
            catch (error) {
                //Polygon is probably self intersecting
            }
            if (tCAGshape != null)
                tCAGarray.push(tCAGshape);
        }
        if (tCAGarray.length > 0) {
            tRes = tCAGarray[0];
            for (let i = 1; i < tCAGarray.length; i++) {
                tRes = tRes.union(tCAGarray[i]);
            }
        }
        return (tRes);
    }
    static getShapeWidth(pMesh) {
        let tmeshBounds = pMesh.getBoundingInfo();
        let tRes = 0;
        let tP1 = new Vector2(tmeshBounds.minimum.x, 0);
        let tP2 = new Vector2(tmeshBounds.maximum.x, 0);
        tRes = Vector2.Distance(tP1, tP2);
        return (tRes);
    }
    static getShapeDepth(pMesh) {
        let tmeshBounds = pMesh.getBoundingInfo();
        let tRes = 0;
        let tP1 = new Vector2(0, tmeshBounds.minimum.z);
        let tP2 = new Vector2(0, tmeshBounds.maximum.z);
        tRes = Vector2.Distance(tP1, tP2);
        return (tRes);
    }
    static getShapeCenter(pMesh) {
        let tmeshBounds = pMesh.getBoundingInfo();
        let tRes = Util.to2d(tmeshBounds.boundingBox.centerWorld);
        return (tRes);
    }
    static getAreaUnitString(pLengthUnit) {
        let tRes = " ";
        switch (pLengthUnit) {
            case 0:
                tRes = " mm²";
                break;
            case 1:
                tRes = " cm²";
                break;
            case 2:
                tRes = " inch²";
                break;
            case 3:
                tRes = " feet²";
                break;
            case 4:
                tRes = " m²";
                break;
            case 5:
                tRes = " km²";
                break;
            default: //3
                tRes = " ";
                break;
        }
        return (tRes);
    }
    static convertVector2ToDimStr(pVector) {
        const tDimX = toLocaleLengthString(pVector.x, this.mApp.i18n.locale, this.mApp.i18n.lengthUnit, true);
        const tDimY = toLocaleLengthString(pVector.y, this.mApp.i18n.locale, this.mApp.i18n.lengthUnit, true);
        const tRes = tDimX + ' x ' + tDimY;
        return (tRes);
    }
    static convertToAreaValue(pCoreApp, pAreaVal) {
        let tRes = pAreaVal;
        // tslint:disable-next-line:switch-default
        switch (pCoreApp.i18n.lengthUnit) {
            case LengthUnit.Millimeter:
                tRes = tRes * 1000000;
                break;
            case LengthUnit.Centimeter:
                tRes = tRes * 10000;
                break;
            case LengthUnit.Inch:
                tRes = tRes * 1550.003;
                break;
            case LengthUnit.Feet:
                tRes = tRes * 10.764;
                break;
            case LengthUnit.Kilometer:
                tRes = tRes * 0.000001;
                break;
        }
        return (tRes);
    }
    static getLengthUnitString(pLengthUnit) {
        let tRes = " ";
        switch (pLengthUnit) {
            case 0:
                tRes = " mm";
                break;
            case 1:
                tRes = " cm";
                break;
            case 2:
                tRes = " inch";
                break;
            case 3:
                tRes = " feet";
                break;
            case 4:
                tRes = " m";
                break;
            case 5:
                tRes = " km";
                break;
            default:
                tRes = " ";
                break;
        }
        return (tRes);
    }
    static sameLineV2(pLine1, pLine2, pDelta = 0.0000001) {
        let tP1Start = pLine1.Start;
        let tP1End = pLine1.End;
        let tP2Start = pLine2.Start;
        let tP2End = pLine2.End;
        let tP11 = Util.samePointsV3(tP1Start, tP2Start, pDelta);
        let tP22 = Util.samePointsV3(tP1End, tP2End, pDelta);
        let tP12 = Util.samePointsV3(tP1Start, tP2End, pDelta);
        let tP21 = Util.samePointsV3(tP1End, tP2Start, pDelta);
        if ((tP11 && tP22) || (tP12 && tP21))
            return (true);
        return (false);
    }
    static sameLineV3(pLine1, pLine2, pDelta = 0.0000001) {
        let tP1Start = pLine1[0];
        let tP1End = pLine1[1];
        let tP2Start = pLine2[0];
        let tP2End = pLine2[1];
        let tP11 = Util.samePointsV3(tP1Start, tP2Start, pDelta);
        let tP22 = Util.samePointsV3(tP1End, tP2End, pDelta);
        let tP12 = Util.samePointsV3(tP1Start, tP2End, pDelta);
        let tP21 = Util.samePointsV3(tP1End, tP2Start, pDelta);
        if ((tP11 && tP22) || (tP12 && tP21))
            return (true);
        return (false);
    }
    static addToOutlineArray(pLine, pArray) {
        if (pArray.length === 0) {
            pArray.push(pLine);
            return;
        }
        let tSL = false;
        let tIdx = -1;
        for (let i = 0; i < pArray.length; i++) {
            if (Util.sameLineV2(pLine, pArray[i])) {
                tSL = true;
                tIdx = i;
                break;
            }
        }
        if (!tSL)
            pArray.push(pLine);
        else {
            pArray.splice(tIdx, 1);
        }
    }
    static convertLonLatToXY(pLon, pLat) {
        let tX = 6371 * Math.cos(pLat) * Math.cos(pLon);
        let tY = 6371 * Math.cos(pLat) * Math.sin(pLon);
        //let tZ = 6371 * Math.sin(pLat);
        let tRes = new Vector2(tX, tY);
        return (tRes);
    }
    static geoDistance(plat1, plon1, plat2, plon2) {
        let radlat1 = Math.PI * plat1 / 180;
        let radlat2 = Math.PI * plat2 / 180;
        let theta = plon1 - plon2;
        let radtheta = Math.PI * theta / 180;
        let dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
        if (dist > 1) {
            dist = 1;
        }
        dist = Math.acos(dist);
        dist = dist * 180 / Math.PI;
        dist = dist * 60 * 1.1515;
        dist = dist * 1.609344 * 1000; //distance in meter
        return dist;
    }
    static _xmlAddRootNode(pDocument, pName, pAttributes) {
        let trootNode = pDocument.appendChild(pDocument.createElement(pName));
        for (let tAttribute of pAttributes) {
            trootNode.setAttribute(tAttribute.qualifiedName, tAttribute.value);
        }
        return (trootNode);
    }
    static _xmlAddChildNode(pDocument, pParentNode, pNodeName, pNodeValue, pAttributes) {
        let tdocumentNode = pDocument.createElement(pNodeName);
        let tchildValue = pDocument.createTextNode("");
        if (pNodeValue.trim() !== "")
            tchildValue = pDocument.createTextNode(pNodeValue);
        let tchildNode = pParentNode.appendChild(tdocumentNode);
        if (pAttributes != null) {
            for (let tAttribute of pAttributes) {
                tchildNode.setAttribute(tAttribute.qualifiedName, tAttribute.value);
            }
        }
        tchildNode.appendChild(tchildValue);
        return (tchildNode);
    }
    static getDirectionToCamera(pPos, pView) {
        let tCameraVec;
        if (pView.cameraControl.cameraMode === CameraMode.Perspective) {
            tCameraVec = pView.cameraControl.position.subtract(pPos);
            tCameraVec.normalize();
        }
        else
            tCameraVec = pView.cameraControl.direction.negate();
        return (tCameraVec);
    }
    static generateUVS(pMesh) {
        let vertexData = new VertexData();
        let indices = pMesh.getIndices();
        let positions = pMesh.getVerticesData(VertexBuffer.PositionKind);
        vertexData.indices = indices;
        vertexData.positions = positions;
        if (vertexData != null) {
            let uvs = [];
            if (vertexData.positions != null && vertexData.indices != null) {
                for (var p = 0; p < vertexData.positions.length / 3; p++) {
                    uvs.push((vertexData.positions[3 * p] - (-4)) / 8, (vertexData.positions[3 * p + 2] - (-4)) / 8);
                }
            }
            vertexData.uvs = uvs;
        }
        vertexData.applyToMesh(pMesh);
        return (pMesh);
    }
    static getPointsofLinePath(pLinePath) {
        let tRes = new Array();
        if (pLinePath.length === 0)
            return (tRes);
        if (pLinePath.length > 0 && (pLinePath[0] === undefined))
            return (tRes);
        if (pLinePath.length > 0)
            tRes.push(pLinePath[0][0], pLinePath[0][1]);
        for (let i = 1; i < pLinePath.length - 1; i++) {
            tRes.push(pLinePath[i][1]);
        }
        tRes.push(pLinePath[0][0]);
        return (tRes);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    static generateBoxUV(pMesh, pMirrorTransform = false) {
        const tindices = pMesh.getIndices();
        const tpdata = pMesh.getVerticesData(VertexBuffer.PositionKind);
        if (tpdata == null || tindices == null) {
            return;
        }
        let tvertexData = new VertexData();
        tvertexData.indices = tindices;
        tvertexData.positions = tpdata;
        const tuvData = [];
        // transform coordiantes
        const tdefMat = Matrix.FromArray([1, 0, 0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 0, 0, 0, 1]);
        // loop mesh triangles
        for (var i = 0; i < tindices.length; i += 3) {
            let tfIdx1 = tindices[i] * 3;
            let tfIdx2 = tindices[i + 1] * 3;
            let tfIdx3 = tindices[i + 2] * 3;
            let v1 = Vector3.FromArray(tpdata, tfIdx1);
            let v2 = Vector3.FromArray(tpdata, tfIdx2);
            let v3 = Vector3.FromArray(tpdata, tfIdx3);
            if (tdefMat) {
                v1 = Vector3.TransformCoordinates(v1, tdefMat);
                v2 = Vector3.TransformCoordinates(v2, tdefMat);
                v3 = Vector3.TransformCoordinates(v3, tdefMat);
            }
            // result tex coords
            let t1 = new Vector2();
            let t2 = new Vector2();
            let t3 = new Vector2();
            let n = Vector3.Cross(v2.subtract(v1), v3.subtract(v1));
            let l = n.length();
            if (l !== 0) {
                n.normalize();
            }
            else {
                n.set(0, 0, 1);
            }
            if (pMirrorTransform) {
                n = n.negate();
            }
            let dom2 = this.dominantAxis(n);
            let u = 1.0;
            if (dom2 === AxisDir.NegativeZ
                || dom2 === AxisDir.PositiveY
                || dom2 === AxisDir.NegativeX) {
                u = -u;
            }
            switch (dom2) {
                case AxisDir.NegativeZ:
                case AxisDir.PositiveZ:
                    t1.set(u * v1.x, v1.y);
                    t2.set(u * v2.x, v2.y);
                    t3.set(u * v3.x, v3.y);
                    break;
                case AxisDir.NegativeY:
                case AxisDir.PositiveY:
                    t1.set(u * v1.x, v1.z);
                    t2.set(u * v2.x, v2.z);
                    t3.set(u * v3.x, v3.z);
                    break;
                case AxisDir.NegativeX:
                case AxisDir.PositiveX:
                    t1.set(u * v1.y, v1.z);
                    t2.set(u * v2.y, v2.z);
                    t3.set(u * v3.y, v3.z);
                    break;
                default:
                    break;
            }
            tuvData[tindices[i] * 2] = t1.x;
            tuvData[tindices[i] * 2 + 1] = t1.y;
            tuvData[tindices[i + 1] * 2] = t2.x;
            tuvData[tindices[i + 1] * 2 + 1] = t2.y;
            tuvData[tindices[i + 2] * 2] = t3.x;
            tuvData[tindices[i + 2] * 2 + 1] = t3.y;
        }
        tvertexData.uvs = tuvData;
        tvertexData.applyToMesh(pMesh);
    }
    static dominantAxis(v) {
        let x = Math.abs(v.x);
        let y = Math.abs(v.y);
        let z = Math.abs(v.z);
        if (x > y && x > z) {
            return (v.x > 0 ? AxisDir.PositiveX : AxisDir.NegativeX);
        }
        const tRes = y > z
            ? (v.y > 0 ? AxisDir.PositiveY : AxisDir.NegativeY)
            : (v.z > 0 ? AxisDir.PositiveZ : AxisDir.NegativeZ);
        return (tRes);
    }
    static getPolygonCentroid(pts) {
        let first = pts[0];
        let last = pts[pts.length - 1];
        if (first.x !== last.x || first.y !== last.y) {
            pts.push(first);
        }
        let twicearea = 0;
        let x = 0;
        let y = 0;
        let nPts = pts.length;
        let p1;
        let p2;
        let f;
        for (let i = 0, j = nPts - 1; i < nPts; j = i++) {
            p1 = pts[i];
            p2 = pts[j];
            f = (p1.y - first.y) * (p2.x - first.x) - (p2.y - first.y) * (p1.x - first.x);
            twicearea += f;
            x += (p1.x + p2.x - 2 * first.x) * f;
            y += (p1.y + p2.y - 2 * first.y) * f;
        }
        f = twicearea * 3;
        let tRes = new Vector3(x / f + first.x, 0, y / f + first.y);
        return (tRes);
    }
    static getPointsofLinePath1(pLinePath) {
        let tRes = new Array();
        if (pLinePath.length === 0)
            return (tRes);
        if ((pLinePath.length === 1) && (pLinePath[0] === undefined))
            return (tRes);
        if (pLinePath.length > 0)
            tRes.push(pLinePath[0][0], pLinePath[0][1]);
        for (let i = 1; i < pLinePath.length - 1; i++) {
            tRes.push(pLinePath[i][1]);
        }
        return (tRes);
    }
    static getLineIntersectionPoint(pX1, pY1, pX2, pY2, pX3, pY3, pX4, pY4) {
        let tRes = false;
        // Check if none of the lines are of length 0
        if ((pX1 === pX2 && pY1 === pY2) || (pY3 === pX4 && pY3 === pY4)) {
            return false;
        }
        let tden = ((pY4 - pY3) * (pX2 - pX1) - (pX4 - pX3) * (pY2 - pY1));
        // parallel
        if (tden === 0) {
            return false;
        }
        let ua = ((pX4 - pX3) * (pY1 - pY3) - (pY4 - pY3) * (pX1 - pX3)) / tden;
        let ub = ((pX2 - pX1) * (pY1 - pY3) - (pY2 - pY1) * (pX1 - pX3)) / tden;
        // intersection along the segments
        if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
            return false;
        }
        // intersection point
        let x = pX1 + ua * (pX2 - pX1);
        let y = pY1 + ua * (pY2 - pY1);
        tRes = new Vector2(x, y);
        return (tRes);
    }
    static getIntersectionPointV2(pP1P1, pP1P2, pP2P1, pP2P2, pExactSegment = true) {
        let tRes = null;
        if (isParallel2(pP1P1, pP1P2, pP2P1, pP2P2))
            return (tRes);
        let tL1P1 = pP1P1;
        let tL1P2 = pP1P2;
        let tL2P1 = pP2P1;
        let tL2P2 = pP2P2;
        let tL1dir = (pP1P2.subtract(pP1P1)).normalize();
        let tL2dir = (pP2P2.subtract(pP2P1)).normalize();
        if (!pExactSegment) {
            tL1P1 = tL1P1.subtract((tL1dir).scale(1000));
            tL1P2 = tL1P2.add(tL1dir.scale(1000));
            tL2P1 = tL2P1.subtract((tL2dir).scale(1000));
            tL2P2 = tL2P2.add(tL2dir.scale(1000));
        }
        // check intersection
        let tintersectionPoint = intersectLineLine2(tL1P1, tL1P2, tL2P1, tL2P2);
        if (tintersectionPoint != null)
            tRes = tintersectionPoint;
        return (tRes);
    }
    /**
     * Checks, whether a point in inside a polygon.
     */
    static pointInPolygon(pPoint, pRoom) {
        let tpolyX = Array();
        let tpolyY = Array();
        for (let tCorner of pRoom) {
            tpolyX.push(tCorner.x);
            tpolyY.push(tCorner.y);
        }
        let tx = pPoint.x;
        let ty = pPoint.y;
        let tj = pRoom.length - 1;
        let toddNodes = false;
        for (let ti = 0; ti < pRoom.length; ti++) {
            if ((tpolyY[ti] < ty && tpolyY[tj] >= ty || tpolyY[tj] < ty && tpolyY[ti] >= ty) &&
                (tpolyX[ti] + (ty - tpolyY[ti]) / (tpolyY[tj] - tpolyY[ti]) * (tpolyX[tj] - tpolyX[ti]) < tx)) {
                toddNodes = !toddNodes;
            }
            tj = ti;
        }
        return toddNodes;
    }
    static pointInMeshFilter(pMesh) {
        let tname = pMesh.name;
        if (tname.indexOf("shape2d_") === 0)
            return true;
        return false;
    }
    /**
     * Checks, whether a point is inside a mesh.
     */
    static pointInMesh(pRayPos, pMesh, pScene) {
        let tRay = new Ray(pRayPos, Vector3.Down());
        let trayHits = pScene.multiPickWithRay(tRay);
        if (trayHits == null || trayHits.length === 0) {
            return (false);
        }
        for (let thit of trayHits) {
            if (thit.pickedMesh === pMesh) {
                return (true);
            }
        }
        return (false);
    }
    static wallInTriangle(pP1, pP2, pTriangle) {
        if (Util.pointInPolygon(pP1, pTriangle))
            return (true);
        return (Util.pointInPolygon(pP2, pTriangle));
    }
    static lineIntersects(pL1P1, pL1P2, pL2P1, pL2P2) {
        let a;
        let b;
        let c;
        let d;
        let p;
        let q;
        let r;
        let s;
        a = pL1P1.x;
        b = pL1P1.y;
        c = pL1P2.x;
        d = pL1P2.y;
        p = pL2P1.x;
        q = pL2P1.y;
        r = pL2P2.x;
        s = pL2P2.y;
        let det;
        let gamma;
        let lambda;
        det = (c - a) * (s - q) - (r - p) * (d - b);
        if (det === 0) {
            return false;
        }
        lambda = ((s - q) * (r - a) + (p - r) * (s - b)) / det;
        gamma = ((b - d) * (r - a) + (c - a) * (s - b)) / det;
        return (0 < lambda && lambda < 1) && (0 < gamma && gamma < 1);
    }
    static intDIV(pDividend, pDivisor) {
        let tRes = pDividend / pDivisor;
        if (tRes >= 0)
            return Math.floor(tRes);
        return Math.ceil(tRes);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    static async collectIdenticalObjects(pSourceArticle, pCompareList) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
        let tArtList = [];
        let titemProps = await pSourceArticle.getItemProperties();
        let tartData = {};
        tartData.catalogId = (_a = titemProps.article) === null || _a === void 0 ? void 0 : _a.catalogId;
        tartData.manufacturerId = (_b = titemProps.article) === null || _b === void 0 ? void 0 : _b.manufacturerId;
        tartData.program = (_c = titemProps.article) === null || _c === void 0 ? void 0 : _c.program;
        tartData.distributionRegion = (_d = titemProps.article) === null || _d === void 0 ? void 0 : _d.distributionRegion;
        tartData.baseArticleNumber = (_e = titemProps.article) === null || _e === void 0 ? void 0 : _e.baseArticleNumber;
        tartData.finalArticleNumber = (_f = titemProps.article) === null || _f === void 0 ? void 0 : _f.finalArticleNumber;
        tartData.variantCode = (_g = titemProps.article) === null || _g === void 0 ? void 0 : _g.variantCode;
        tArtList.push(pSourceArticle);
        for (let tElement of pCompareList) {
            if (tElement instanceof MainArticleElement) {
                let tMA = tElement;
                if (pSourceArticle === tMA)
                    continue;
                let tMA_ofmlData = await tMA.getItemProperties();
                let tMA_artData = {};
                tMA_artData.catalogId = (_h = tMA_ofmlData.article) === null || _h === void 0 ? void 0 : _h.catalogId;
                tMA_artData.manufacturerId = (_j = tMA_ofmlData.article) === null || _j === void 0 ? void 0 : _j.manufacturerId;
                tMA_artData.program = (_k = tMA_ofmlData.article) === null || _k === void 0 ? void 0 : _k.program;
                tMA_artData.distributionRegion = (_l = tMA_ofmlData.article) === null || _l === void 0 ? void 0 : _l.distributionRegion;
                tMA_artData.baseArticleNumber = (_m = tMA_ofmlData.article) === null || _m === void 0 ? void 0 : _m.baseArticleNumber;
                tMA_artData.finalArticleNumber = (_o = tMA_ofmlData.article) === null || _o === void 0 ? void 0 : _o.finalArticleNumber;
                tMA_artData.variantCode = (_p = tMA_ofmlData.article) === null || _p === void 0 ? void 0 : _p.variantCode;
                if (tMA_artData.catalogId === tartData.catalogId &&
                    tMA_artData.manufacturerId === tartData.manufacturerId &&
                    tMA_artData.program === tartData.program &&
                    tMA_artData.distributionRegion === tartData.distributionRegion &&
                    tMA_artData.baseArticleNumber === tartData.baseArticleNumber &&
                    tMA_artData.finalArticleNumber === tartData.finalArticleNumber &&
                    tMA_artData.variantCode === tartData.variantCode) {
                    tArtList.push(tMA);
                }
            }
        }
        return (tArtList);
    }
}
export var GridFieldPointMode;
(function (GridFieldPointMode) {
    GridFieldPointMode[GridFieldPointMode["all"] = 0] = "all";
    GridFieldPointMode[GridFieldPointMode["full"] = 1] = "full";
    GridFieldPointMode[GridFieldPointMode["diagonalTopLeft"] = 2] = "diagonalTopLeft";
    GridFieldPointMode[GridFieldPointMode["diagonalTopRight"] = 3] = "diagonalTopRight";
    GridFieldPointMode[GridFieldPointMode["diagnoalBottomLeft"] = 4] = "diagnoalBottomLeft";
    GridFieldPointMode[GridFieldPointMode["diagnoalBottomRight"] = 5] = "diagnoalBottomRight";
    GridFieldPointMode[GridFieldPointMode["horizontalTop"] = 6] = "horizontalTop";
    GridFieldPointMode[GridFieldPointMode["horizontalBottom"] = 7] = "horizontalBottom";
    GridFieldPointMode[GridFieldPointMode["verticalLeft"] = 8] = "verticalLeft";
    GridFieldPointMode[GridFieldPointMode["verticalRight"] = 9] = "verticalRight";
})(GridFieldPointMode || (GridFieldPointMode = {}));
export var PointOnPolygons;
(function (PointOnPolygons) {
    PointOnPolygons[PointOnPolygons["none"] = 0] = "none";
    PointOnPolygons[PointOnPolygons["some"] = 1] = "some";
    PointOnPolygons[PointOnPolygons["all"] = 2] = "all";
})(PointOnPolygons || (PointOnPolygons = {}));
